import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RiRobotLine } from 'react-icons/ri';
import { HiCalendarDays, HiClock, HiMapPin, HiUser } from 'react-icons/hi2';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import TeacherSelector from './ui/TeacherSelector';
import SubjectSelector from './ui/SubjectSelector';
import GroupSelector from './ui/GroupSelector';
import ScheduleEntryWeekPicker from './ui/ScheduleEntryWeekPicker';
import EditScheduleItemTimeSection from './EditScheduleItemTimeSection';
import ScheduleEntryDetailView from './ScheduleEntryDetailView';
import WeekChangeConfirmationModal from './ui/WeekChangeConfirmationModal';
import Popover from './ui/Popover';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useActiveDays } from '../hooks/useActiveDays';
import { validateWeekSeries } from '../utils/scheduleUtils';
import { getWeekNumber } from '../utils/helpers';
import ScheduleEntry from '../models/ScheduleEntry';
import HelpIcon from './Documentation/HelpIcon';
import AssetManager from '../services/AssetManager';
import { conflictManager } from '../services/ConflictManager';
import TeacherModel from '../models/Teacher';
import GroupModel from '../models/Group';
import SubjectModel from '../models/Subject';
import AutoScheduler from './AutoScheduler';
import { ScheduleSuggestion } from '../types/AutoScheduling';
import logger from '../utils/logger';
import AcademicYearService from '../services/AcademicYearService';
import {
    getWeeksOutsideResolvedPeriod,
    intersectWeekSeriesWithPeriod,
    parseAcademicYearStart,
    resolveScheduleEntryPeriod
} from '../utils/schedulePeriodResolver';

// Wrapper to satisfy TS JSX typing with react-icons under current React/TS versions
const RobotIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = RiRobotLine as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

// Icon wrappers for preview modal
const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = HiCalendarDays as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = HiClock as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

const MapPinIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = HiMapPin as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

const UserIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = HiUser as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

// Type definitions
interface Group extends GroupModel { }

interface Subject extends SubjectModel { }

interface Teacher extends TeacherModel { }

interface Room {
    id: number;
    name?: string;
    room_code?: string;
    public_visible?: boolean | null;
    publicVisible?: boolean;
}

interface InstitutionSettings {
    minHour: number;
    maxHour: number;
}

interface ConflictReason {
    reason: string;
    severity: 'red' | 'orange' | 'green';
}

interface PopoverPosition {
    x: number;
    y: number;
}

interface ExceptionInfo {
    originalId: string;
    week: number;
    year: number;
}

interface EditFormData {
    id?: string | number;
    subject?: string;
    course?: string;
    teacher?: string; // Legacy single teacher (for backward compatibility)
    teachers?: (number | string | { id: number; name?: string; role?: string; })[]; // NEW: Multiple teachers
    host?: string; // Host for events (instead of teacher)
    groups?: (number | string | { id: number; name?: string; group_code?: string; })[];
    room?: string;
    day?: string;
    weekSeries?: string;
    period_id?: number | string | null;
    periodId?: number | string | null;
    title?: string;
    eventName?: string;
    description?: string;
    type?: string;
    time?: string;
    timeStart?: string;
    timeEnd?: string;
    _baseScheduleItems?: ScheduleEntry[];
    _teachers?: TeacherModel[];
    _groups?: GroupModel[];
    _subjects?: SubjectModel[];
    [key: string]: any;
}

interface EditScheduleItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedScheduleItem: ScheduleEntry | null;
    onSaveEdit: (editedData: any, originalItem?: ScheduleEntry) => void;
    onDelete?: (item: ScheduleEntry) => void;
    groups?: GroupModel[];
    subjects?: SubjectModel[];
    teachers?: TeacherModel[];
    rooms?: Room[];
    currentYear: number;
    currentDate: Date;
    isAdmin?: boolean;
    baseScheduleItems?: ScheduleEntry[];
    // Teacher management
    onAddTeacher?: (teacherData: { name: string; email: string; teacher_code: string }) => Promise<any>;
    // Subject management
    onAddSubject?: (subjectData: { name: string; color?: string; associatedRooms?: string[] }) => Promise<any>;
    // Group management
    onAddGroup?: (groupData: { name: string; group_code?: string }) => Promise<any>;
    // Group-specific mode props
    groupSpecificMode?: {
        groupData: GroupModel;
        directPublish?: boolean; // If true, bypasses review queue and publishes directly
        disableGroupRemoval?: boolean; // If true, prevents removing the specified group from selection
    };
    // Documentation/demo support: render the modal content inline without overlay
    renderInline?: boolean;
    modalContainerClassName?: string;
    // Time range context for new entries
    currentTimeRangeContext?: {
        selectedPeriodId?: number | null;
        selectedMonth?: {
            month: string;
            weekSeries: string;
            weekRange: { start: number; end: number };
            calendarYear: number;
        } | null;
        selectedWeek?: {
            week: number;
            year: number;
        } | null;
        timePeriods?: any[];
        periodSelectionDisabled?: boolean;
        periodSelectionDisabledReason?: string;
    };
    // Draft mode props
    draftMode?: boolean; // If true, we're in draft mode
    onSaveEditImmediate?: (editedData: any, originalItem?: ScheduleEntry) => void; // For immediate saving in draft mode
}

const EditScheduleItemModal: React.FC<EditScheduleItemModalProps> = ({
    isOpen,
    onClose,
    selectedScheduleItem,
    onSaveEdit,
    onDelete,
    groups = [],
    subjects = [],
    teachers = [],
    rooms = [],
    currentYear,
    currentDate,
    isAdmin = false,
    baseScheduleItems = [],
    onAddTeacher,
    onAddSubject,
    onAddGroup,
    groupSpecificMode,
    renderInline = false,
    modalContainerClassName = '',
    currentTimeRangeContext,
    draftMode = false,
    onSaveEditImmediate
}) => {
    // Get active days from AssetManager
    const { activeDays } = useActiveDays();
    const [editFormData, setEditFormData] = useState<EditFormData | null>(null);
    const [localEditTimeValue, setLocalEditTimeValue] = useState<string>('');
    const [isLocalTimeValid, setIsLocalTimeValid] = useState<boolean>(true);
    const [showDetailView, setShowDetailView] = useState<boolean>(false);
    const [institutionSettings, setInstitutionSettings] = useState<InstitutionSettings>({ minHour: 8, maxHour: 18 });

    // Local state to track subjects, teachers, and groups for dropdown updates
    const [localSubjects, setLocalSubjects] = useState<SubjectModel[]>(subjects);
    const [localTeachers, setLocalTeachers] = useState<TeacherModel[]>(teachers);
    const [localGroups, setLocalGroups] = useState<GroupModel[]>(groups);
    const [roomFilter, setRoomFilter] = useState<string>('');

    // Conflict detection state
    const [conflicts, setConflicts] = useState<ConflictReason[]>([]);
    const [showConflictPopover, setShowConflictPopover] = useState<boolean>(false);
    const [popoverPosition, setPopoverPosition] = useState<PopoverPosition>({ x: 0, y: 0 });
    const conflictIconRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [academicYearStructure, setAcademicYearStructure] = useState<any | null>(null);

    // Use snackbar notifications instead of toast
    const { showSuccess, showWarning, showError } = useSnackbar();

    // Ref to track if time change is from user input (to prevent useEffect override)
    const timeChangeFromUserRef = useRef<boolean>(false);

    // Create AssetManager instance - memoized to prevent recreating on every render
    const assetManager = useMemo(() => AssetManager.getInstance(), []);

    useEffect(() => {
        let cancelled = false;
        AcademicYearService.getInstance().getAcademicYearStructure()
            .then((structure) => {
                if (!cancelled) {
                    setAcademicYearStructure(structure);
                }
            })
            .catch((error) => {
                logger.warn('[EditScheduleItemModal] Failed to load academic year structure:', error);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const periodResolutionStructure = useMemo(() => {
        if (!academicYearStructure) {
            return null;
        }

        const contextPeriods = currentTimeRangeContext?.timePeriods;
        if (!contextPeriods || contextPeriods.length === 0) {
            return academicYearStructure;
        }

        const existingPeriodIds = new Set((academicYearStructure.periods || []).map((period: any) => String(period.id)));
        const mergedContextPeriods = contextPeriods.filter((period: any) => !existingPeriodIds.has(String(period.id)));

        return {
            ...academicYearStructure,
            periods: [
                ...(academicYearStructure.periods || []),
                ...mergedContextPeriods
            ]
        };
    }, [academicYearStructure, currentTimeRangeContext?.timePeriods]);

    const getAcademicYearStartForForm = useCallback(() => {
        return parseAcademicYearStart(academicYearStructure?.academicYear) || currentYear;
    }, [academicYearStructure, currentYear]);

    const isPeriodSelectionDisabled = Boolean(currentTimeRangeContext?.periodSelectionDisabled);
    const lockedPeriodId = isPeriodSelectionDisabled && currentTimeRangeContext?.selectedPeriodId != null
        ? Number(currentTimeRangeContext.selectedPeriodId)
        : null;

    const getCurrentContextPeriodRef = useCallback(() => {
        if (lockedPeriodId !== null && !Number.isNaN(lockedPeriodId)) {
            return lockedPeriodId;
        }
        return currentTimeRangeContext?.selectedPeriodId
            ? Number(currentTimeRangeContext.selectedPeriodId)
            : null;
    }, [currentTimeRangeContext?.selectedPeriodId, lockedPeriodId]);

    const resolvedEditPeriod = useMemo(() => {
        const periodId = editFormData?.period_id ?? editFormData?.periodId;
        if (periodId) {
            return resolveScheduleEntryPeriod(periodId, periodResolutionStructure);
        }

        return resolveScheduleEntryPeriod(null, periodResolutionStructure);
    }, [editFormData?.period_id, editFormData?.periodId, periodResolutionStructure]);

    const outOfPeriodWeeks = useMemo(() => (
        getWeeksOutsideResolvedPeriod(editFormData?.weekSeries, resolvedEditPeriod)
    ), [editFormData?.weekSeries, resolvedEditPeriod]);

    const editFormPeriodOptions = useMemo(() => {
        const raw = periodResolutionStructure?.periods || [];
        return raw
            .map((p: any) => {
                const id = p?.id;
                if (id === undefined || id === null) return null;
                const n = Number(id);
                if (Number.isNaN(n)) return null;
                const name = p.name || `Period ${n}`;
                const sw = p.start_week ?? p.startWeek;
                const ew = p.end_week ?? p.endWeek;
                const weekSuffix =
                    sw != null && ew != null ? ` (Week ${sw}–${ew})` : '';
                return { id: n, label: `${name}${weekSuffix}` };
            })
            .filter(Boolean) as { id: number; label: string }[];
    }, [periodResolutionStructure]);

    const classEditPeriodSelect = useMemo(() => {
        const currentPidRaw = editFormData?.period_id ?? editFormData?.periodId;
        const currentPid =
            currentPidRaw !== null && currentPidRaw !== undefined && currentPidRaw !== ''
                ? Number(currentPidRaw)
                : null;
        const pidValid = currentPid !== null && !Number.isNaN(currentPid);
        const periodOptionIds = new Set(editFormPeriodOptions.map((o) => o.id));
        const showOrphan = Boolean(pidValid && currentPid !== null && !periodOptionIds.has(currentPid));

        let selectValue = '';
        if (pidValid && currentPid !== null) {
            selectValue = String(currentPid);
        }

        return {
            selectValue,
            showOrphan,
            orphanPeriodId: showOrphan ? currentPid! : null,
        };
    }, [editFormData?.period_id, editFormData?.periodId, editFormPeriodOptions]);

    // New state for week change confirmation modal
    const [showWeekChangeModal, setShowWeekChangeModal] = useState<boolean>(false);
    const [pendingEditData, setPendingEditData] = useState<EditFormData | null>(null);
    const [originalScheduleData, setOriginalScheduleData] = useState<EditFormData | null>(null);

    // Exception handling state
    const [originalEntry, setOriginalEntry] = useState<ScheduleEntry | null>(null);
    const [exceptionInfo, setExceptionInfo] = useState<ExceptionInfo | null>(null);

    // AutoScheduler state
    const [showAutoScheduler, setShowAutoScheduler] = useState<boolean>(false);

    // Exception creation mode state
    const [exceptionCreationWeek, setExceptionCreationWeek] = useState<number | null>(null);

    // Event preview modal state
    const [showEventPreview, setShowEventPreview] = useState<boolean>(false);

    const handleClose = useCallback(() => {
        setEditFormData(null);
        setLocalEditTimeValue('');
        setIsLocalTimeValid(true);
        setShowDetailView(false);
        setShowWeekChangeModal(false);
        setPendingEditData(null);
        setOriginalScheduleData(null);
        setOriginalEntry(null);
        setExceptionInfo(null);
        setShowAutoScheduler(false);
        setExceptionCreationWeek(null);
        setShowEventPreview(false);
        setRoomFilter('');
        // Clear conflict state
        setConflicts([]);
        setShowConflictPopover(false);
        onClose();
    }, [onClose]);

    // Handler for when user wants to create/edit an exception for a specific week
    const handleCreateExceptionForWeek = useCallback((weekNumber: number) => {
        if (!selectedScheduleItem || !editFormData) {
            logger.warn('[EditScheduleItemModal] Cannot create exception: missing selected item or form data');
            return;
        }

        // Find or use the original entry
        let rootEntry = originalEntry;
        if (!rootEntry) {
            // If we don't have an original entry reference, try to find it in baseScheduleItems
            rootEntry = baseScheduleItems.find(item => String(item.id) === String(selectedScheduleItem.id)) || null;
            if (!rootEntry) {
                // If still not found, use the current selected item as the root (for new entries)
                rootEntry = selectedScheduleItem;
            }
        }

        // Set exception info to indicate we're editing an exception for this week
        const newExceptionInfo: ExceptionInfo = {
            originalId: String(rootEntry.id),
            week: weekNumber,
            year: currentYear
        };

        setExceptionInfo(newExceptionInfo);
        setOriginalEntry(rootEntry);
        setExceptionCreationWeek(weekNumber);

        logger.info(`[EditScheduleItemModal] Entering exception editing mode for week ${weekNumber}`);

        // Show notification to user
        showSuccess(`Now editing exception for week ${weekNumber}. Changes will only apply to this week.`);
    }, [selectedScheduleItem, editFormData, originalEntry, baseScheduleItems, currentYear, showSuccess]);

    // Helper function to check if this is an exception entry
    const isExceptionEntry = useCallback((item: ScheduleEntry | null): boolean => {
        if (!item || !item.id) return false;
        // Exception entries have IDs like "16451-w26y2025"
        return /^\d+-w\d+y\d+$/.test(String(item.id));
    }, []);

    // Helper function to extract original ID and exception info from exception entry
    const parseExceptionId = useCallback((exceptionId: string | number): ExceptionInfo | null => {
        if (!exceptionId) return null;
        const match = String(exceptionId).match(/^(\d+)-w(\d+)y(\d+)$/);
        if (!match || !match[1] || !match[2] || !match[3]) return null;
        return {
            originalId: match[1],
            week: parseInt(match[2], 10),
            year: parseInt(match[3], 10)
        };
    }, []);

    // Helper function to check if time-sensitive fields have changed
    const haveTimeSensitiveFieldsChanged = useCallback((original: EditFormData, edited: EditFormData): boolean => {
        const timeParts = edited.time ? edited.time.split(' - ') : [];
        const newTimeStart = timeParts[0]?.trim() || '';
        const newTimeEnd = timeParts[1]?.trim() || '';

        return (
            original.timeStart !== newTimeStart ||
            original.timeEnd !== newTimeEnd ||
            original.teacher !== edited.teacher ||
            original.room !== edited.room
        );
    }, []);

    // Helper function to handle exception edits
    const handleExceptionEdit = useCallback((editedDataObj: EditFormData, editScope: 'thisWeekOnly' | 'allWeeks', applyAllFieldsAsException: boolean = false): ScheduleEntry => {
        if (!originalEntry || !exceptionInfo) {
            throw new Error('Missing originalEntry or exceptionInfo for exception edit');
        }

        const { week, year } = exceptionInfo;

        // Parse the time input
        const timeParts = editedDataObj.time ? editedDataObj.time.split(' - ') : [];
        const timeStart = timeParts[0]?.trim() || '';
        const timeEnd = timeParts[1]?.trim() || '';

        if (editScope === 'thisWeekOnly') {
            // If applyAllFieldsAsException is true (for new exception creation),
            // apply ALL fields as exception changes
            let exceptionChanges: any;
            let updatedRootEntry: ScheduleEntry;

            if (applyAllFieldsAsException) {
                // Apply ALL fields as exception changes for this week only
                exceptionChanges = {
                    timeStart: timeStart,
                    timeEnd: timeEnd,
                    // Only set multi-teacher array for exception scope
                    teachers: (editedDataObj as any).teachers,
                    room: editedDataObj.room,
                    day: editedDataObj.day,
                    // Include any other fields that might have changed
                    ...(editedDataObj.subject && { subject: editedDataObj.subject }),
                    ...(editedDataObj.course && { course: editedDataObj.course }),
                    ...(editedDataObj.groups && { groups: editedDataObj.groups })
                };

                // Root entry stays unchanged
                updatedRootEntry = new ScheduleEntry({
                    ...originalEntry.toObject()
                } as any);
            } else {
                // Apply time-sensitive fields as exception, other fields to root
                exceptionChanges = {
                    timeStart: timeStart,
                    timeEnd: timeEnd,
                    // Only set multi-teacher array for exception scope
                    teachers: (editedDataObj as any).teachers,
                    room: editedDataObj.room
                };

                // Create updated root entry with non-time-sensitive fields
                const rootUpdates = { ...editedDataObj };
                delete rootUpdates.timeStart;
                delete rootUpdates.timeEnd;
                delete rootUpdates.teacher;
                delete rootUpdates.room;
                delete rootUpdates.time;
                delete rootUpdates.id; // Keep original ID

                updatedRootEntry = new ScheduleEntry({
                    ...originalEntry.toObject(),
                    ...rootUpdates
                } as any);
            }

            // Add the exception
            return updatedRootEntry.addException({
                week: week,
                year: year,
                changes: exceptionChanges
            });

        } else if (editScope === 'allWeeks') {
            // Apply all changes to root entry and remove the exception
            const updatedRootEntry = new ScheduleEntry({
                ...originalEntry.toObject(),
                ...editedDataObj,
                timeStart: timeStart,
                timeEnd: timeEnd,
                id: originalEntry.id // Keep original ID
            } as any);

            // Remove the specific exception for this week if it exists
            if (Array.isArray(originalEntry.exceptions) && originalEntry.exceptions.length > 0) {
                const updatedExceptions = originalEntry.exceptions.filter(
                    ex => !(ex.week === week && ex.year === year)
                );
                return new ScheduleEntry({
                    ...updatedRootEntry.toObject(),
                    exceptions: updatedExceptions.length > 0 ? updatedExceptions : null
                });
            }

            return updatedRootEntry;
        }

        throw new Error(`Unknown editScope: ${editScope}`);
    }, [originalEntry, exceptionInfo]);

    // Helper function to remove exception for the current week
    const handleRemoveException = useCallback(() => {
        if (!originalEntry || !exceptionInfo) {
            logger.warn('[EditScheduleItemModal] Cannot remove exception: missing original entry or exception info');
            return;
        }

        const { week, year } = exceptionInfo;

        // Confirm with user
        const confirmRemove = window.confirm(
            `Are you sure you want to remove the exception for week ${week}?\n\n` +
            `This will revert the schedule entry for week ${week} back to the default values.`
        );

        if (!confirmRemove) {
            return;
        }

        // Remove the exception from the original entry
        if (Array.isArray(originalEntry.exceptions) && originalEntry.exceptions.length > 0) {
            const updatedExceptions = originalEntry.exceptions.filter(
                ex => !(ex.week === week && ex.year === year)
            );

            const updatedEntry = new ScheduleEntry({
                ...originalEntry.toObject(),
                exceptions: updatedExceptions.length > 0 ? updatedExceptions : null
            });

            logger.info(`[EditScheduleItemModal] Removing exception for week ${week}`);

            // Call the appropriate save handler based on draft mode
            // We'll pass a special marker to indicate this is an exception removal
            const removalData = {
                ...updatedEntry.toObject(),
                _removalContext: {
                    isExceptionRemoval: true,
                    removedWeek: week,
                    removedYear: year
                }
            };

            if (draftMode && onSaveEditImmediate) {
                onSaveEditImmediate(removalData, originalEntry);
            } else {
                onSaveEdit(removalData, originalEntry);
            }

            showSuccess(`Exception removed for week ${week}`);
            handleClose();
        } else {
            logger.warn('[EditScheduleItemModal] No exceptions found to remove');
            showError('No exceptions found to remove');
        }
    }, [originalEntry, exceptionInfo, onSaveEdit, onSaveEditImmediate, draftMode, handleClose, showSuccess, showError]);

    // Utility functions to convert date to schedule entry format
    const convertDateToScheduleFormat = useCallback((date: string): { weekSeries: string; day: string; period_id?: number | null } => {
        const contextPeriodId = getCurrentContextPeriodRef();
        if (!date) return { weekSeries: '', day: '', period_id: contextPeriodId };

        // Parse input date (YYYY-MM-DD) as local date to avoid timezone shifts
        const [yStr = '', mStr = '', dStr = ''] = date.split('-');
        const y = parseInt(yStr, 10);
        const m = parseInt(mStr, 10);
        const d = parseInt(dStr, 10);
        if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
            return { weekSeries: '', day: '', period_id: contextPeriodId };
        }
        const selectedDate = new Date(y, (m - 1), d);

        const weekNumber = getWeekNumber(selectedDate);
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[selectedDate.getDay()];

        return {
            weekSeries: weekNumber.toString(),
            day: dayName || '',
            period_id: contextPeriodId
        };
    }, [getCurrentContextPeriodRef]);

    const convertScheduleFormatToDate = useCallback((weekSeries: string, day: string, periodId?: number | string | null): string => {
        if (!weekSeries || !day) return '';

        const firstWeek = weekSeries.split(',')[0];
        if (!firstWeek) return '';

        const weekNumber = parseInt(firstWeek, 10); // Take first week if multiple
        if (isNaN(weekNumber)) return '';

        // New rows may have period_id instead of legacy year; resolve to the academic-year start for date math.
        const resolvedPeriodForDate = periodId ? resolveScheduleEntryPeriod(periodId, periodResolutionStructure) : null;
        const academicYearStart = resolvedPeriodForDate?.academicYearStart || getAcademicYearStartForForm();
        let targetYear = academicYearStart;
        const academicYearService = AcademicYearService.getInstance();

        // Try to get cached structure first for synchronous resolution
        const parsedYear = academicYearService.getParsedAcademicYear();
        if (parsedYear && parsedYear.weekToYearMap.has(weekNumber)) {
            // If we have cached data, use it.
            if (parsedYear.startCalendarYear === academicYearStart) {
                targetYear = parsedYear.weekToYearMap.get(weekNumber)!;
            }
        } else {
            // Fallback logic if cache is missing (rare but possible)
            // If week number is small (e.g., < 20) and we're in the context of academic year start, 
            // it's likely the next calendar year.
            // Assuming standard academic year starting in Aug/Sept.
            if (weekNumber < 20) {
                targetYear = academicYearStart + 1;
            }
        }

        // Calculate date from week number and day using targetYear
        const jan4 = new Date(targetYear, 0, 4);
        const dayOfWeek = jan4.getDay() || 7;
        const week1Monday = new Date(jan4);
        week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

        const targetMonday = new Date(week1Monday);
        targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

        const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.toLowerCase());
        if (dayIndex === -1) return '';

        const targetDate = new Date(targetMonday);
        // Sunday is at index 0, but it's the last day of the week, so we add 6 days from Monday
        // For other days, we add (dayIndex - 1) days from Monday
        if (dayIndex === 0) {
            targetDate.setDate(targetMonday.getDate() + 6); // Sunday is 6 days after Monday
        } else {
            targetDate.setDate(targetMonday.getDate() + (dayIndex - 1)); // Other days
        }

        // Return local YYYY-MM-DD (avoid toISOString which applies UTC offset)
        const yyyy = targetDate.getFullYear();
        const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
        const dd = String(targetDate.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }, [getAcademicYearStartForForm, periodResolutionStructure]);

    // Helper to determine if a single week is selected (e.g., "12" or "12-12")
    const isSingleWeekSelected = useCallback((weekSeries?: string): boolean => {
        if (!weekSeries) return false;
        const s = weekSeries.trim();
        if (!s) return false;
        if (s.includes(',')) return false;
        if (s.includes('-')) {
            const parts = s.split('-').map(p => parseInt(p.trim(), 10));
            if (parts.length !== 2) return false;
            const [a, b] = parts;
            return !Number.isNaN(a) && !Number.isNaN(b) && a === b;
        }
        const n = parseInt(s, 10);
        return !Number.isNaN(n);
    }, []);

    // Helper function to convert HH:MM:SS to HH:MM format
    const convertTimeToHhMm = useCallback((timeStr: string): string => {
        if (!timeStr) return timeStr;

        // If already HH:MM format, return as is
        if (/^\d{2}:\d{2}$/.test(timeStr)) {
            return timeStr;
        }

        // If HH:MM:SS format, remove seconds
        if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) {
            return timeStr.substring(0, 5); // Remove last 3 characters (":ss")
        }

        return timeStr; // Return original if format doesn't match
    }, []);

    // Time validation function
    const validateTimeFormat = useCallback((timeStr: string): boolean => {
        if (!timeStr) return false;

        // Check format: "hh:mm - hh:mm" or "hh:mm:ss - hh:mm:ss" (convert seconds to minutes)
        const isValidFormat = /^\d{2}:\d{2}(:\d{2})?\s*-\s*\d{2}:\d{2}(:\d{2})?$/.test(timeStr);
        if (!isValidFormat) return false;

        // Parse start and end times
        const parts = timeStr.split('-').map(part => part.trim());
        if (parts.length !== 2) return false;

        const [startTimeStr, endTimeStr] = parts;
        if (!startTimeStr || !endTimeStr) return false;

        // Convert to minutes for comparison
        const startParts = startTimeStr.split(':');
        const endParts = endTimeStr.split(':');

        if (startParts.length < 2 || endParts.length < 2) return false;

        const startHours = parseInt(startParts[0] || '', 10);
        const startMinutes = parseInt(startParts[1] || '', 10);
        const endHours = parseInt(endParts[0] || '', 10);
        const endMinutes = parseInt(endParts[1] || '', 10);

        if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) return false;

        const startTotalMinutes = startHours * 60 + startMinutes;
        const endTotalMinutes = endHours * 60 + endMinutes;

        // End time must be after start time
        return endTotalMinutes > startTotalMinutes;
    }, []);

    // Sync local state with props when they change
    useEffect(() => {
        setLocalSubjects(subjects);
    }, [subjects]);

    useEffect(() => {
        setLocalTeachers(teachers);
    }, [teachers]);

    useEffect(() => {
        setLocalGroups(groups);
    }, [groups]);

    // Wrapper functions to update local state when adding new items
    const handleAddSubjectWrapper = useCallback(async (subjectData: { name: string; color?: string; associatedRooms?: string[] }) => {
        if (!onAddSubject) {
            throw new Error('Add subject functionality is not available');
        }

        const newSubject = await onAddSubject(subjectData);

        // Update local subjects state immediately (only if newSubject is returned)
        if (newSubject) {
            setLocalSubjects(prev => [...prev, newSubject]);
        }

        return newSubject;
    }, [onAddSubject]);

    const handleAddTeacherWrapper = useCallback(async (teacherData: { name: string; email: string; teacher_code: string }) => {
        if (!onAddTeacher) {
            throw new Error('Add teacher functionality is not available');
        }

        const newTeacher = await onAddTeacher(teacherData);

        // Update local teachers state immediately (only if newTeacher is returned)
        if (newTeacher) {
            setLocalTeachers(prev => [...prev, newTeacher]);
        }

        return newTeacher;
    }, [onAddTeacher]);

    const handleAddGroupWrapper = useCallback(async (groupData: { name: string; group_code?: string }) => {
        if (!onAddGroup) {
            throw new Error('Add group functionality is not available');
        }

        const newGroup = await onAddGroup(groupData);

        // Update local groups state immediately (only if newGroup is returned)
        if (newGroup) {
            setLocalGroups(prev => [...prev, newGroup]);
        }

        return newGroup;
    }, [onAddGroup]);

    // Fetch institution settings when modal opens
    useEffect(() => {
        if (isOpen && isAdmin) {
            const fetchInstitutionSettings = async () => {
                try {
                    const settings = await assetManager.fetchInstitutionSettings();
                    setInstitutionSettings({
                        minHour: settings.minHour || 8,
                        maxHour: settings.maxHour || 18
                    });
                } catch (error) {
                    logger.error('[EditScheduleItemModal] Error fetching institution settings:', error);
                    // Keep default values on error
                }
            };

            fetchInstitutionSettings();
        }
    }, [isOpen, isAdmin, assetManager]);

    // Initialize form data when selectedScheduleItem changes
    useEffect(() => {


        // Prevent re-initializing if the selected item is the same
        if (selectedScheduleItem?.id === editFormData?.id) {
            return;
        }

        if (selectedScheduleItem && isAdmin && selectedScheduleItem.id) {
            // Check if this is an exception entry
            const isException = isExceptionEntry(selectedScheduleItem);
            let originalEntryRef: ScheduleEntry | null = null;
            let exceptionInfoRef: ExceptionInfo | null = null;

            if (isException) {
                // Parse exception information
                exceptionInfoRef = parseExceptionId(selectedScheduleItem.id);
                if (exceptionInfoRef) {
                    // Find the original entry
                    originalEntryRef = baseScheduleItems.find(item => item.id === exceptionInfoRef!.originalId) || null;
                    if (originalEntryRef) {
                        logger.debug('[EditScheduleItemModal] Found original entry for exception:', originalEntryRef.id);
                    } else {
                        logger.warn('[EditScheduleItemModal] Could not find original entry with ID:', exceptionInfoRef.originalId);
                    }
                }
            } else if ((selectedScheduleItem as any)._exceptionContext) {
                // The caller preserved root id but attached exception context; use it to set exception state
                const ctx = (selectedScheduleItem as any)._exceptionContext as { originalId: string | number; week: number; year: number };
                if (ctx && ctx.originalId != null && typeof ctx.week === 'number' && typeof ctx.year === 'number') {
                    exceptionInfoRef = {
                        originalId: String(ctx.originalId),
                        week: ctx.week,
                        year: ctx.year
                    };
                    originalEntryRef = baseScheduleItems.find(item => String(item.id) === String(ctx.originalId)) || null;
                    if (!originalEntryRef) {
                        logger.warn('[EditScheduleItemModal] Exception context provided but original entry not found:', ctx.originalId);
                    } else {
                        logger.debug('[EditScheduleItemModal] Using provided exception context for week:', ctx.week);
                    }
                }
            } else {
                // Not an exception-specific open: still try to find the root entry so we can surface its exceptions in the week picker
                const rootById = baseScheduleItems.find(item => String(item.id) === String(selectedScheduleItem.id)) || null;
                if (rootById) {
                    originalEntryRef = rootById;
                } else if ((selectedScheduleItem as any).exceptions && Array.isArray((selectedScheduleItem as any).exceptions) && (selectedScheduleItem as any).exceptions.length > 0) {
                    // Fallback: if the selected item itself carries exceptions, use it directly
                    originalEntryRef = selectedScheduleItem as any;
                }
            }

            setOriginalEntry(originalEntryRef);
            setExceptionInfo(exceptionInfoRef);

            const initialData: EditFormData = {
                ...selectedScheduleItem as any,
                // Ensure we use the correct property name
                weekSeries: selectedScheduleItem.weekSeries || '',
                period_id: (selectedScheduleItem as any).period_id ?? (selectedScheduleItem as any).periodId ?? null,
                // Initialize host field for events/meetings (may come from teacher field for existing items)
                host: selectedScheduleItem.host
                    || ((selectedScheduleItem.type === 'event' || selectedScheduleItem.type === 'meeting') ? selectedScheduleItem.teacher : '')
                    || ''
            };

            // Automatically convert timeStart and timeEnd from HH:MM:SS to HH:MM format
            if (initialData.timeStart) {
                initialData.timeStart = convertTimeToHhMm(initialData.timeStart);
            }
            if (initialData.timeEnd) {
                initialData.timeEnd = convertTimeToHhMm(initialData.timeEnd);
            }
            if (initialData.time) {
                // Convert the combined time string as well
                if (initialData.time.includes(' - ')) {
                    const parts = initialData.time.split(' - ');
                    if (parts.length === 2 && parts[0] && parts[1]) {
                        const startTime = convertTimeToHhMm(parts[0].trim());
                        const endTime = convertTimeToHhMm(parts[1].trim());
                        initialData.time = `${startTime} - ${endTime}`;
                    }
                } else {
                    initialData.time = convertTimeToHhMm(initialData.time);
                }
            }

            // Initialize association objects (subject/teacher/room) for new object-based selects
            const subjObj = (selectedScheduleItem as any).subjectObj;
            if (subjObj && subjObj.id !== undefined) {
                (initialData as any).subject = { id: subjObj.id, name: subjObj.name };
                (initialData as any).subject_id = subjObj.id;
                // maintain legacy course string for display
                initialData.course = subjObj.name || initialData.course || '';
            } else if (selectedScheduleItem.subject && typeof selectedScheduleItem.subject === 'string') {
                // Handle case where subject is passed as a string (e.g., from drag-to-create)
                const matchingSubject = localSubjects.find(s => s.name === selectedScheduleItem.subject);
                if (matchingSubject) {
                    (initialData as any).subject = { id: matchingSubject.id, name: matchingSubject.name };
                    (initialData as any).subject_id = matchingSubject.id;
                    initialData.course = matchingSubject.name;
                    logger.debug('[EditScheduleItemModal] Found matching subject for string subject:', {
                        subjectString: selectedScheduleItem.subject,
                        matchedSubject: matchingSubject
                    });
                } else {
                    logger.warn('[EditScheduleItemModal] No matching subject found for string subject:', selectedScheduleItem.subject);
                }
            }

            // Handle legacy single teacher
            const teacherObj = (selectedScheduleItem as any).teacherObj;
            if (teacherObj && teacherObj.id !== undefined) {
                (initialData as any).teacher = { id: teacherObj.id, name: teacherObj.name };
                (initialData as any).teacher_id = teacherObj.id;
            } else if (selectedScheduleItem.teacher && typeof selectedScheduleItem.teacher === 'string') {
                // Handle case where teacher is passed as a string (e.g., from drag-to-create)
                const matchingTeacher = localTeachers.find(t => t.name === selectedScheduleItem.teacher);
                if (matchingTeacher) {
                    (initialData as any).teacher = { id: matchingTeacher.id, name: matchingTeacher.name };
                    (initialData as any).teacher_id = matchingTeacher.id;
                    logger.debug('[EditScheduleItemModal] Found matching teacher for string teacher:', {
                        teacherString: selectedScheduleItem.teacher,
                        matchedTeacher: matchingTeacher
                    });
                } else {
                    logger.warn('[EditScheduleItemModal] No matching teacher found for string teacher:', selectedScheduleItem.teacher);
                }
            }

            // Handle multiple teachers (NEW)
            const teachersArray = (selectedScheduleItem as any).teachersArray || (selectedScheduleItem as any).teachers;
            if (teachersArray && Array.isArray(teachersArray) && teachersArray.length > 0) {
                // Convert teachers array to format expected by form
                (initialData as any).teachers = teachersArray.map((teacher: any) => ({
                    id: teacher.id,
                    name: teacher.name,
                    role: teacher.ScheduleEntryTeachers?.role || 'primary'
                }));

                logger.debug('[EditScheduleItemModal] Initialized multiple teachers:', (initialData as any).teachers);
            } else if ((initialData as any).teacher) {
                // If we only have a single teacher, create a teachers array with that teacher
                (initialData as any).teachers = [{
                    id: (initialData as any).teacher.id,
                    name: (initialData as any).teacher.name,
                    role: 'primary'
                }];
            }

            const roomObj = (selectedScheduleItem as any).roomObj;
            if (roomObj && roomObj.id !== undefined) {
                (initialData as any).room = { id: roomObj.id, name: roomObj.name || roomObj.room_code };
                (initialData as any).room_id = roomObj.id;
            } else if (selectedScheduleItem.room && typeof selectedScheduleItem.room === 'string') {
                // Handle case where room is passed as a string (e.g., from drag-to-create)
                // Find the room object by name or room_code
                const matchingRoom = rooms.find(r =>
                    r.name === selectedScheduleItem.room ||
                    r.room_code === selectedScheduleItem.room
                );
                if (matchingRoom) {
                    (initialData as any).room = { id: matchingRoom.id, name: matchingRoom.name || matchingRoom.room_code };
                    (initialData as any).room_id = matchingRoom.id;
                    logger.debug('[EditScheduleItemModal] Found matching room for string room:', {
                        roomString: selectedScheduleItem.room,
                        matchedRoom: matchingRoom
                    });
                } else {
                    logger.warn('[EditScheduleItemModal] No matching room found for string room:', selectedScheduleItem.room);
                }
            }

            // For events, set default groups to empty (meaning "All Groups")
            if (initialData.type === 'event' && (!initialData.groups || initialData.groups.length === 0)) {
                initialData.groups = [];
            }

            // For meetings, enforce no groups
            if (initialData.type === 'meeting') {
                initialData.groups = [];
            }

            // Handle group-specific mode initialization
            if (groupSpecificMode?.groupData?.id !== undefined) {
                // Ensure the specified group is always included in the groups array
                const groupId = groupSpecificMode.groupData.id;
                if (!initialData.groups || !Array.isArray(initialData.groups)) {
                    initialData.groups = [groupId];
                } else {
                    // Check if the group is already included
                    const hasGroup = initialData.groups.some(g => {
                        if (typeof g === 'object' && g !== null) {
                            return (g as any).id === groupId;
                        }
                        return g === groupId;
                    });

                    if (!hasGroup) {
                        initialData.groups = [groupId, ...initialData.groups];
                    }
                }

                // For new items in group-specific mode, set default to class type
                if (String(selectedScheduleItem.id).startsWith('temp-add-') && !initialData.type) {
                    initialData.type = 'class';
                }
            }

            // If this is a new item created via drag-and-drop and has drag context, use that for initial date
            if ((selectedScheduleItem as any)._dragContext && String(selectedScheduleItem.id).startsWith('temp-add-')) {
                const { currentWeekNumber, currentYear: dragYear, contextWeekRange } = (selectedScheduleItem as any)._dragContext;

                // Priority 1: Use contextWeekRange if available (this contains the context-appropriate week range)
                if (contextWeekRange) {
                    initialData.weekSeries = contextWeekRange;
                    logger.debug('[EditScheduleItemModal] Using contextWeekRange from drag:', contextWeekRange);
                } else if (currentWeekNumber && dragYear) {
                    // Priority 2: Fallback to current week from drag context
                    initialData.weekSeries = currentWeekNumber.toString();
                    logger.debug('[EditScheduleItemModal] Using currentWeekNumber from drag:', currentWeekNumber);
                }

                const contextPeriodId = getCurrentContextPeriodRef();
                initialData.period_id = contextPeriodId;
            }
            // If this is a new item and we have current time range context, use that for week series
            else if (String(selectedScheduleItem.id).startsWith('temp-add-') && currentTimeRangeContext) {
                logger.debug('[EditScheduleItemModal] Using current time range context for new entry:', currentTimeRangeContext);

                // Priority 1: Use selected week if available (single week selection)
                if (currentTimeRangeContext.selectedWeek && currentTimeRangeContext.selectedWeek.week) {
                    initialData.weekSeries = currentTimeRangeContext.selectedWeek.week.toString();
                    logger.debug('[EditScheduleItemModal] Using selected week:', currentTimeRangeContext.selectedWeek.week);
                }
                // Priority 2: Use selected month's week series if available
                else if (currentTimeRangeContext.selectedMonth) {
                    initialData.weekSeries = currentTimeRangeContext.selectedMonth.weekSeries;
                    logger.debug('[EditScheduleItemModal] Using selected month week series:', currentTimeRangeContext.selectedMonth.weekSeries);
                }
                // Priority 3: Use selected period's week range if available
                else if (currentTimeRangeContext.selectedPeriodId && currentTimeRangeContext.timePeriods) {
                    const selectedPeriod = currentTimeRangeContext.timePeriods.find(p => String(p.id) === String(currentTimeRangeContext.selectedPeriodId));
                    if (selectedPeriod && selectedPeriod.start_week && selectedPeriod.end_week) {
                        if (selectedPeriod.start_week === selectedPeriod.end_week) {
                            initialData.weekSeries = selectedPeriod.start_week.toString();
                        } else {
                            initialData.weekSeries = `${selectedPeriod.start_week}-${selectedPeriod.end_week}`;
                        }
                        logger.debug('[EditScheduleItemModal] Using selected period week series:', initialData.weekSeries);
                    }
                }
                // Priority 4: If no specific context is selected, leave weekSeries empty (no selection)
                else {
                    initialData.weekSeries = '';
                    logger.debug('[EditScheduleItemModal] No specific time context selected, leaving weekSeries empty');
                }

                const contextPeriodId = getCurrentContextPeriodRef();
                initialData.period_id = contextPeriodId;
            }

            logger.debug('[EditScheduleItemModal] Setting editFormData to:', initialData);
            setEditFormData(initialData);
            // Store original data for comparison
            setOriginalScheduleData(initialData);
            // Don't clear originalEntry and exceptionInfo here - they were set correctly above for exceptions
            setRoomFilter('');
        } else {
            logger.debug('[EditScheduleItemModal] Clearing form data');
            setEditFormData(null);
            setOriginalScheduleData(null);
            setOriginalEntry(null);
            setExceptionInfo(null);
            setRoomFilter('');
        }
    }, [selectedScheduleItem?.id, isAdmin, baseScheduleItems, isExceptionEntry, parseExceptionId, editFormData, currentTimeRangeContext, localSubjects, localTeachers, getAcademicYearStartForForm, getCurrentContextPeriodRef]);

    // Update local time value when form data changes (but only from external sources, not user input)
    useEffect(() => {
        logger.debug('[EditScheduleItemModal] Time useEffect triggered', {
            editFormData: !!editFormData,
            editFormDataId: editFormData?.id,
            timeChangeFromUser: timeChangeFromUserRef.current,
            currentLocalTimeValue: localEditTimeValue
        });

        if (editFormData) {
            // Debug: Log the groups data structure
            logger.debug('editFormData.groups:', editFormData.groups);
            if (editFormData.groups && editFormData.groups.length > 0) {
                logger.debug('First group item type:', typeof editFormData.groups[0]);
                logger.debug('First group item:', editFormData.groups[0]);
            }

            let newTimeValue = '';

            // Prefer current form data time over the original item's display
            if (editFormData.timeStart && editFormData.timeEnd) {
                newTimeValue = `${editFormData.timeStart} - ${editFormData.timeEnd}`;
            } else if (editFormData.time) {
                newTimeValue = editFormData.time;
            } else if (selectedScheduleItem && selectedScheduleItem.id && typeof (selectedScheduleItem as any).timeRangeDisplay === 'function') {
                newTimeValue = (selectedScheduleItem as any).timeRangeDisplay();
            }

            // Automatically convert HH:MM:SS format to HH:MM format
            if (newTimeValue && newTimeValue.includes(' - ')) {
                const parts = newTimeValue.split(' - ');
                if (parts.length === 2 && parts[0] && parts[1]) {
                    const startTime = convertTimeToHhMm(parts[0].trim());
                    const endTime = convertTimeToHhMm(parts[1].trim());
                    newTimeValue = `${startTime} - ${endTime}`;
                }
            } else if (newTimeValue) {
                // Handle single time value (for partial input)
                newTimeValue = convertTimeToHhMm(newTimeValue);
            }

            logger.debug('[EditScheduleItemModal] Time values:', {
                newTimeValue,
                currentLocalTimeValue: localEditTimeValue,
                willUpdate: !timeChangeFromUserRef.current && newTimeValue !== localEditTimeValue
            });

            // Only update local time value if this is not from user input AND the value actually needs to change
            // AND we don't already have a valid local time value (to preserve user changes)
            // Also check if the new time value is different from what we already have
            if (!timeChangeFromUserRef.current && (!localEditTimeValue || !isLocalTimeValid) && newTimeValue && newTimeValue !== localEditTimeValue) {
                logger.debug('[EditScheduleItemModal] Updating local time value from', localEditTimeValue, 'to', newTimeValue);
                setLocalEditTimeValue(newTimeValue);
                const isValid = validateTimeFormat(newTimeValue);
                setIsLocalTimeValid(isValid);
            } else if (!timeChangeFromUserRef.current && !newTimeValue && localEditTimeValue) {
                // Clear if no time value and we currently have one
                logger.debug('[EditScheduleItemModal] Clearing local time value');
                setLocalEditTimeValue('');
                setIsLocalTimeValid(false);
            } else {
                logger.debug('[EditScheduleItemModal] Skipping time update - user input detected, valid local time exists, or no change needed');
            }
        } else if (localEditTimeValue || !isLocalTimeValid) {
            // Only clear if we actually have something to clear
            logger.debug('[EditScheduleItemModal] Clearing time value - no editFormData');
            setLocalEditTimeValue('');
            setIsLocalTimeValid(true);
        }
    }, [editFormData, selectedScheduleItem, localEditTimeValue, validateTimeFormat, isLocalTimeValid]);

    const handleEditFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        logger.debug('[EditScheduleItemModal] Form field changed:', { name, value });

        if (name !== 'time') {
            setEditFormData(prevData => {
                const newData = prevData ? { ...prevData, [name]: value } : null;
                logger.debug('[EditScheduleItemModal] Updated form data:', newData);
                return newData;
            });
        }
    }, []);

    const handleTimeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        let newTimeValue = e.target.value;

        // Convert HH:MM:SS format to HH:MM format
        if (newTimeValue.includes(' - ')) {
            const parts = newTimeValue.split(' - ');
            if (parts.length === 2 && parts[0] && parts[1]) {
                const startTime = convertTimeToHhMm(parts[0].trim());
                const endTime = convertTimeToHhMm(parts[1].trim());
                newTimeValue = `${startTime} - ${endTime}`;
            }
        } else {
            // Handle single time value (for partial input)
            newTimeValue = convertTimeToHhMm(newTimeValue);
        }

        timeChangeFromUserRef.current = true; // Mark as user input
        setLocalEditTimeValue(newTimeValue);
        setIsLocalTimeValid(validateTimeFormat(newTimeValue));
    }, [validateTimeFormat, convertTimeToHhMm]);

    const handleTimeBlur = useCallback(() => {
        if (isLocalTimeValid && editFormData && editFormData.time !== localEditTimeValue) {
            // Parse the time to also update timeStart and timeEnd
            const timeParts = localEditTimeValue.split(' - ');
            const timeStart = timeParts[0]?.trim() || '';
            const timeEnd = timeParts[1]?.trim() || '';

            setEditFormData(prevData => prevData ? {
                ...prevData,
                time: localEditTimeValue,
                timeStart: timeStart,
                timeEnd: timeEnd
            } : null);
            logger.debug("Time updated in main state (onBlur):", localEditTimeValue);
        } else if (!isLocalTimeValid) {
            logger.warn("Invalid time format on blur:", localEditTimeValue);
        }

        // Reset the flag after blur processing
        setTimeout(() => {
            timeChangeFromUserRef.current = false;
        }, 100);
    }, [isLocalTimeValid, editFormData, localEditTimeValue]);

    // Handle event date change
    const handleEventDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedDate = e.target.value;
        if (selectedDate) {
            const scheduleFormat = convertDateToScheduleFormat(selectedDate);
            setEditFormData(prev => ({
                ...prev!,
                weekSeries: scheduleFormat.weekSeries,
                day: scheduleFormat.day,
                period_id: scheduleFormat.period_id
            }));
        }
    }, [convertDateToScheduleFormat]);

    const handleEditPeriodSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        if (isPeriodSelectionDisabled) {
            return;
        }
        const v = e.target.value;
        const nextPeriodId = v === '' ? null : Number(v);
        setEditFormData((prev) => {
            if (!prev) return null;
            const resolved = resolveScheduleEntryPeriod(nextPeriodId, periodResolutionStructure);
            const nextWeekSeries = intersectWeekSeriesWithPeriod(prev.weekSeries, resolved);
            return { ...prev, period_id: nextPeriodId, weekSeries: nextWeekSeries };
        });
    }, [isPeriodSelectionDisabled, periodResolutionStructure]);

    useEffect(() => {
        if (!isPeriodSelectionDisabled || lockedPeriodId === null || Number.isNaN(lockedPeriodId)) {
            return;
        }

        setEditFormData((prev) => {
            if (!prev) return null;
            const currentPid = prev.period_id ?? prev.periodId;
            if (String(currentPid) === String(lockedPeriodId)) {
                return prev;
            }
            const resolved = resolveScheduleEntryPeriod(lockedPeriodId, periodResolutionStructure);
            const nextWeekSeries = intersectWeekSeriesWithPeriod(prev.weekSeries, resolved);
            return { ...prev, period_id: lockedPeriodId, weekSeries: nextWeekSeries };
        });
    }, [isPeriodSelectionDisabled, lockedPeriodId, periodResolutionStructure]);

    // Handle type change
    const handleTypeChange = useCallback((newType: string) => {
        setEditFormData(prev => {
            const updatedData = { ...prev!, type: newType };

            // When switching to event, clear groups (meaning "All Groups") and subject
            if (newType === 'event') {
                updatedData.groups = [];
                updatedData.subject = '';
                updatedData.course = ''; // Backward compatibility
                updatedData.title = updatedData.title || updatedData.subject || updatedData.course || '';
                // Add event name if not present
                if (!updatedData.eventName && !updatedData.title) {
                    updatedData.eventName = prev!.subject || prev!.course || '';
                }
            }
            // When switching to meeting, enforce no groups and use free-text subject via title
            else if (newType === 'meeting') {
                updatedData.groups = [];
                updatedData.eventName = '';
                // Keep subject/course empty; rely on title as the meeting subject/topic
                updatedData.subject = '';
                updatedData.course = '';
                if (!updatedData.title && (prev?.subject || prev?.course)) {
                    updatedData.title = prev?.subject || prev?.course || '';
                }
            }
            // When switching to class, ensure we have a subject
            else if (newType === 'class') {
                // Clear event name and ensure subject is set
                updatedData.eventName = '';
                if (!updatedData.subject && !updatedData.course && updatedData.title) {
                    updatedData.subject = updatedData.title;
                    updatedData.course = updatedData.title; // Backward compatibility
                }
            }

            return updatedData;
        });
    }, []);

    const handleSaveEdit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!isLocalTimeValid) {
            showError("Invalid time format.");
            return;
        }

        // For events using date picker, weekSeries should already be set correctly
        if (editFormData?.type === 'class' && editFormData.weekSeries && !validateWeekSeries(editFormData.weekSeries)) {
            showError("Invalid week series format. Please use comma-separated week numbers (1-53) or ranges (e.g., '38,44' or '52-4')");
            return;
        }

        if (!editFormData || !selectedScheduleItem || !selectedScheduleItem.id) return;

        // Parse the time input (format: "09:00 - 10:30")
        const timeParts = localEditTimeValue.split(' - ');
        if (timeParts.length !== 2 || !timeParts[0] || !timeParts[1]) {
            showError("Invalid time format. Please use 'HH:MM - HH:MM' format.");
            return;
        }

        const editedDataObj: EditFormData = {
            ...editFormData,
            timeStart: timeParts[0].trim(),
            timeEnd: timeParts[1].trim(),
            time: localEditTimeValue, // Include the time field for comparison
            // Add marker for direct publish mode
            _directPublish: groupSpecificMode?.directPublish || false
        };


        // Handle multiple teachers (NEW)
        if ((editedDataObj as any).teachers && Array.isArray((editedDataObj as any).teachers)) {
            const teachersArray = (editedDataObj as any).teachers;
            if (teachersArray.length > 0) {
                (editedDataObj as any).teachers = teachersArray;

                // Set primary teacher for legacy compatibility
                const primaryTeacher = teachersArray[0];
                if (primaryTeacher) {
                    (editedDataObj as any).teacher_id = primaryTeacher.id;
                    editedDataObj.teacher = primaryTeacher.name;
                }

                logger.debug('[EditScheduleItemModal] Prepared multiple teachers for submission:', {
                    teachers: (editedDataObj as any).teachers,
                    primary_teacher: primaryTeacher
                });
            }
        } else if (editedDataObj.teacher) {
            // Legacy single teacher handling
            const matchedTeacher = teachers.find(t => t.name === editedDataObj.teacher);
            if (matchedTeacher && (matchedTeacher as any).id) {
                (editedDataObj as any).teacher_id = (matchedTeacher as any).id;
            }
        }

        // For events, ensure proper field mapping
        if (editFormData.type === 'event') {
            // Use eventName or title as the name for events
            editedDataObj.title = editFormData.eventName || editFormData.title || '';
            editedDataObj.subject = ''; // Events don't have subjects
            editedDataObj.course = ''; // Backward compatibility
            editedDataObj.teacher = editFormData.host || ''; // Keep for compatibility
            // Store host field for frontend use
            editedDataObj.host = editFormData.host || '';
            // Clear teacher arrays for events
            editedDataObj.teachers = [];
            // Remove teacher ID to prevent backend validation errors
            delete editedDataObj.teacher_id;
            // Empty groups array means "All Groups"
            if (!editedDataObj.groups || editedDataObj.groups.length === 0) {
                editedDataObj.groups = [];
            }
        }
        // For meetings, map free-text subject to title, enforce no groups, optional host, invited teachers list
        else if (editFormData.type === 'meeting') {
            editedDataObj.title = editFormData.title || editFormData.subject || '';
            editedDataObj.subject = '';
            editedDataObj.course = '';
            editedDataObj.teacher = editFormData.host || '';
            editedDataObj.host = editFormData.host || '';
            // Meetings should not have groups
            editedDataObj.groups = [];
        }

        const isCreatingNew = selectedScheduleItem?.id ? String(selectedScheduleItem.id).startsWith('temp-add-') : false;
        const actionType = isCreatingNew ? 'Created' : 'Updated';



        // Handle exception entries internally
        if (originalEntry && exceptionInfo) {
            logger.debug('[EditScheduleItemModal] Processing exception entry edit');

            // If this is a new exception being created (exceptionCreationWeek is set),
            // apply ALL changes as exception changes for this week only
            if (exceptionCreationWeek) {
                logger.debug('[EditScheduleItemModal] Creating new exception for week', exceptionCreationWeek);

                // Apply ALL changes to the exception (not just time-sensitive ones)
                const updatedOriginalEntry = handleExceptionEdit(editedDataObj, 'thisWeekOnly', true);

                if (draftMode && onSaveEditImmediate) {
                    onSaveEditImmediate(updatedOriginalEntry, originalEntry);
                } else {
                    onSaveEdit(updatedOriginalEntry, originalEntry);
                }

                logger.debug('[EditScheduleItemModal] Created exception for week', exceptionCreationWeek);
                handleClose();
            }
            // If editing an existing exception, check if time-sensitive fields have changed
            else if (originalScheduleData && haveTimeSensitiveFieldsChanged(originalScheduleData, editedDataObj)) {
                logger.debug('[EditScheduleItemModal] Exception entry with time-sensitive changes detected, showing confirmation modal');
                setPendingEditData(editedDataObj);
                setShowWeekChangeModal(true);
                // DO NOT show notification here - notification should only be shown after user confirms choice
            } else {
                // No time-sensitive changes, apply all changes to the exception
                const updatedOriginalEntry = handleExceptionEdit(editedDataObj, 'thisWeekOnly');

                if (draftMode && onSaveEditImmediate) {
                    onSaveEditImmediate(updatedOriginalEntry, originalEntry);
                } else {
                    onSaveEdit(updatedOriginalEntry, originalEntry);
                }

                logger.debug('[EditScheduleItemModal] Applied non-time-sensitive changes to exception for this week only');

                // Parent component will handle notifications
                handleClose();
            }
        } else {
            // No exception handling needed, proceed with normal save
            if (draftMode && onSaveEditImmediate) {
                // In draft mode, save immediately
                onSaveEditImmediate(editedDataObj, selectedScheduleItem);
            } else {
                // In live mode, use the change tracking system
                onSaveEdit(editedDataObj, selectedScheduleItem);
            }

            // Parent component will handle notifications
            handleClose();
        }
    }, [editFormData, selectedScheduleItem, localEditTimeValue, isLocalTimeValid, onSaveEdit, onSaveEditImmediate, draftMode, originalEntry, exceptionInfo, originalScheduleData, haveTimeSensitiveFieldsChanged, handleExceptionEdit, handleClose]);

    // Handle week change confirmation modal responses
    const handleWeekChangeConfirm = useCallback((choice: 'thisWeekOnly' | 'allWeeks') => {
        if (!pendingEditData || !originalEntry || !exceptionInfo) return;

        try {
            const updatedOriginalEntry = handleExceptionEdit(pendingEditData, choice);

            setShowWeekChangeModal(false);
            setPendingEditData(null);

            // Call the appropriate save handler based on draft mode
            if (draftMode && onSaveEditImmediate) {
                onSaveEditImmediate(updatedOriginalEntry, originalEntry);
            } else {
                onSaveEdit(updatedOriginalEntry, originalEntry);
            }

            // Parent component will handle notifications
            handleClose();
        } catch (error) {
            logger.error('[EditScheduleItemModal] Error handling exception edit:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            showError(`Error updating: ${errorMessage}`);
        }
    }, [pendingEditData, originalEntry, exceptionInfo, handleExceptionEdit, onSaveEdit, onSaveEditImmediate, draftMode, handleClose]);

    const handleWeekChangeCancel = useCallback(() => {
        logger.debug('[EditScheduleItemModal] Week change modal cancelled by user');
        setShowWeekChangeModal(false);
        setPendingEditData(null);
        // NOTE: No notifications should be shown when user cancels
    }, []);

    const handleDeleteClick = useCallback(() => {
        if (onDelete && selectedScheduleItem) {
            onDelete(selectedScheduleItem);
        }
    }, [onDelete, selectedScheduleItem]);

    // AutoScheduler handlers
    const handleOpenAutoScheduler = useCallback(() => {
        // Only allow auto-scheduling for classes with required fields
        if (!editFormData?.teacher || !editFormData?.subject || !editFormData?.groups?.length) {
            showError("Please select a teacher, subject, and at least one group before using auto-scheduling.");
            return;
        }

        // Log the item being excluded from conflict detection
        if (selectedScheduleItem?.id) {
            console.log('[EditScheduleItemModal] Opening AutoScheduler with excludeItemId:', selectedScheduleItem.id);
        }

        setShowAutoScheduler(true);
    }, [editFormData, showError, selectedScheduleItem]);

    const handleCloseAutoScheduler = useCallback(() => {
        setShowAutoScheduler(false);
    }, []);

    // Event preview handlers
    const handleOpenEventPreview = useCallback(() => {
        setShowEventPreview(true);
    }, []);

    const handleCloseEventPreview = useCallback(() => {
        setShowEventPreview(false);
    }, []);

    const handleSuggestionSelect = useCallback((suggestion: ScheduleSuggestion) => {
        if (!editFormData) return;

        // Map room name to room object/id if available for the room <select>
        const matchingRoom = rooms.find(r => (r.name === suggestion.room) || (r.room_code === suggestion.room));

        // Apply the suggestion to the form data
        const updatedFormData = {
            ...editFormData,
            day: suggestion.day,
            timeStart: suggestion.timeStart,
            timeEnd: suggestion.timeEnd,
            time: `${suggestion.timeStart} - ${suggestion.timeEnd}`,
            room: matchingRoom ? { id: matchingRoom.id, name: matchingRoom.name || matchingRoom.room_code } : suggestion.room,
            room_id: matchingRoom ? matchingRoom.id : (editFormData as any).room_id,
            weekSeries: (suggestion.weeks && suggestion.weeks.length > 0
                ? suggestion.weeks.join(',')
                : suggestion.week.toString())
        } as any;

        setEditFormData(updatedFormData);

        // Update the local time value to reflect the new time
        const newTimeValue = `${suggestion.timeStart} - ${suggestion.timeEnd}`;
        setLocalEditTimeValue(newTimeValue);
        setIsLocalTimeValid(true);

        // Close the AutoScheduler
        setShowAutoScheduler(false);

        // Show success message
        showSuccess(`Applied suggestion: ${suggestion.day} ${suggestion.timeStart}-${suggestion.timeEnd} in ${suggestion.room}`);
    }, [editFormData, rooms, showSuccess]);

    const filteredRooms = useMemo(() => {
        if (!rooms || rooms.length === 0) return [];

        const normalizedFilter = roomFilter.trim().toLowerCase();
        let candidates = normalizedFilter
            ? rooms.filter(room => {
                const name = room.name?.toLowerCase() || '';
                const code = room.room_code?.toLowerCase() || '';
                return name.includes(normalizedFilter) || code.includes(normalizedFilter);
            })
            : rooms;

        const selectedRoomId = (editFormData as any)?.room?.id ?? (editFormData as any)?.room_id;
        if (selectedRoomId !== undefined && selectedRoomId !== null) {
            const selectedRoom = rooms.find(room => String(room.id) === String(selectedRoomId));
            if (selectedRoom && !candidates.some(room => String(room.id) === String(selectedRoomId))) {
                candidates = [selectedRoom, ...candidates];
            }
        }

        return candidates;
    }, [rooms, roomFilter, editFormData]);

    const handleRoomFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setRoomFilter(e.target.value);
    }, []);

    const handleRoomFilterKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!filteredRooms.length) return;
        if (e.key === 'Enter') {
            e.preventDefault();
            const room = filteredRooms[0];
            if (!room || !room.id) return;

            setEditFormData(prev => prev ? ({
                ...prev,
                room: { id: room.id, name: room.name || room.room_code },
                room_id: room.id
            } as any) : prev);

            setRoomFilter(room.name || room.room_code || '');
        }
    }, [filteredRooms]);

    // Helper function to calculate duration in minutes
    const calculateDurationInMinutes = useCallback((startTime: string, endTime: string): number => {
        if (!startTime || !endTime) return 90; // Default 90 minutes

        const parseTime = (timeStr: string): number => {
            const parts = timeStr.split(':').map(Number);
            const hours = parts[0] || 0;
            const minutes = parts[1] || 0;
            return hours * 60 + minutes;
        };

        const startMinutes = parseTime(startTime);
        const endMinutes = parseTime(endTime);

        return endMinutes > startMinutes ? endMinutes - startMinutes : 90;
    }, []);

    // Helper function to format event date for preview (same as EventContainer)
    const formatEventDate = useCallback((weekSeries: string, day: string, periodId?: number | string | null): string => {
        if (!weekSeries || !day) return 'TBD';

        try {
            const weekNumber = parseInt(weekSeries.split(',')[0] || '0', 10);
            if (isNaN(weekNumber)) return 'TBD';

            const resolvedPeriodForDate = periodId ? resolveScheduleEntryPeriod(periodId, periodResolutionStructure) : null;
            const academicYearStart = resolvedPeriodForDate?.academicYearStart || getAcademicYearStartForForm();
            let targetYear = academicYearStart;
            const academicYearService = AcademicYearService.getInstance();

            // Try to get cached structure first for synchronous resolution
            const parsedYear = academicYearService.getParsedAcademicYear();
            if (parsedYear && parsedYear.weekToYearMap.has(weekNumber)) {
                if (parsedYear.startCalendarYear === academicYearStart) {
                    targetYear = parsedYear.weekToYearMap.get(weekNumber)!;
                }
            } else {
                // Fallback heuristic
                if (weekNumber < 20) {
                    targetYear = academicYearStart + 1;
                }
            }

            // Calculate date from week number and day
            const jan4 = new Date(targetYear, 0, 4);
            const dayOfWeek = jan4.getDay() || 7;
            const week1Monday = new Date(jan4);
            week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

            const targetMonday = new Date(week1Monday);
            targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

            const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.toLowerCase());
            if (dayIndex === -1) return 'TBD';

            const targetDate = new Date(targetMonday);
            if (dayIndex === 0) {
                targetDate.setDate(targetMonday.getDate() + 6);
            } else {
                targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
            }

            return targetDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting event date:', error);
            return 'TBD';
        }
    }, [getAcademicYearStartForForm, periodResolutionStructure]);

    // Helper function to detect if text contains HTML
    const isHtmlContent = useCallback((text: string): boolean => {
        if (!text || typeof text !== 'string') return false;
        const htmlTagRegex = /<[^>]+>/;
        return htmlTagRegex.test(text);
    }, []);

    // Helper function to sanitize HTML content (basic sanitization)
    const sanitizeHtml = useCallback((html: string): string => {
        if (!html) return '';

        // Remove potentially dangerous tags and attributes
        let sanitized = html
            .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
            .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '') // Remove iframe tags
            .replace(/<object[^>]*>.*?<\/object>/gi, '') // Remove object tags
            .replace(/<embed[^>]*>.*?<\/embed>/gi, '') // Remove embed tags
            .replace(/<link[^>]*>/gi, '') // Remove link tags
            .replace(/<meta[^>]*>/gi, '') // Remove meta tags
            .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
            .replace(/javascript:/gi, '') // Remove javascript: protocols
            .replace(/data:/gi, '') // Remove data: protocols
            .replace(/vbscript:/gi, ''); // Remove vbscript: protocols

        // Ensure links have proper styling and security attributes
        sanitized = sanitized.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
            // Remove any existing style attributes and add our own
            const cleanAttributes = attributes
                .replace(/style="[^"]*"/gi, '')
                .replace(/target="[^"]*"/gi, '')
                .replace(/rel="[^"]*"/gi, '');

            return `<a ${cleanAttributes} style="color: #2563eb; text-decoration: underline;" target="_blank" rel="noopener noreferrer">`;
        });

        return sanitized;
    }, []);

    // Render text with HTML support, clickable links, and preserved newlines
    const renderTextWithLinks = useCallback((text: string) => {
        if (!text) return null;

        // Check if content is HTML
        if (isHtmlContent(text)) {
            const sanitizedHtml = sanitizeHtml(text);
            return (
                <div
                    className="text-sm text-gray-800 leading-relaxed break-words [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800 [&_a]:break-words"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
            );
        }

        // Original plain text handling with links
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const nodes = [];
        let lastIndex = 0;
        let match;
        let key = 0;
        while ((match = urlRegex.exec(text)) !== null) {
            const start = match.index;
            const url = match[0];
            if (start > lastIndex) {
                nodes.push(text.slice(lastIndex, start));
            }
            nodes.push(
                <a
                    key={`url-${key++}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-words"
                >
                    {url}
                </a>
            );
            lastIndex = start + url.length;
        }
        if (lastIndex < text.length) {
            nodes.push(text.slice(lastIndex));
        }
        const withLineBreaks: React.ReactNode[] = [];
        nodes.forEach((chunk, idx) => {
            if (typeof chunk === 'string') {
                const lines = chunk.split('\n');
                lines.forEach((line, li) => {
                    if (line) withLineBreaks.push(<React.Fragment key={`t-${idx}-${li}`}>{line}</React.Fragment>);
                    if (li < lines.length - 1) withLineBreaks.push(<br key={`br-${idx}-${li}`} />);
                });
            } else {
                withLineBreaks.push(chunk);
            }
        });
        return withLineBreaks;
    }, [isHtmlContent, sanitizeHtml]);


    // Function to check for conflicts across all weeks in the series (centralized logic)
    const checkConflicts = useCallback((formData: EditFormData) => {

        // Only check conflicts for class type
        if (!formData || formData.type === 'event' || !isAdmin) {
            logger.debug('[EditScheduleItemModal] Skipping conflict check - not class type or not admin');
            setConflicts([]);
            return;
        }

        // Extract the arrays from enhanced form data
        const scheduleItems = formData._baseScheduleItems || [];
        const teachersList = formData._teachers || [];
        const groupsList = formData._groups || [];
        const subjectsList = formData._subjects || [];

        try {
            // Parse time value
            const timeParts = localEditTimeValue ? localEditTimeValue.split(' - ') : [];
            if (timeParts.length !== 2 || !timeParts[0] || !timeParts[1]) {
                logger.debug('[EditScheduleItemModal] Skipping conflict check - invalid time format:', localEditTimeValue);
                setConflicts([]);
                return;
            }

            const timeStart = timeParts[0].trim();
            const timeEnd = timeParts[1].trim();

            // Parse the week series to get all weeks that need to be checked
            const weekSeries = formData.weekSeries || '';
            const weeksToCheck = new Set<number>();

            if (weekSeries) {
                const parts = weekSeries.split(',').map(part => part.trim());
                parts.forEach(part => {
                    if (part.includes('-')) {
                        const rangeParts = part.split('-');
                        if (rangeParts.length === 2 && rangeParts[0] && rangeParts[1]) {
                            const start = parseInt(rangeParts[0].trim());
                            const end = parseInt(rangeParts[1].trim());

                            if (!isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53) {
                                if (start <= end) {
                                    for (let i = start; i <= end; i++) {
                                        weeksToCheck.add(i);
                                    }
                                } else {
                                    // Handle year crossing (e.g., 52-4)
                                    for (let i = start; i <= 53; i++) {
                                        weeksToCheck.add(i);
                                    }
                                    for (let i = 1; i <= end; i++) {
                                        weeksToCheck.add(i);
                                    }
                                }
                            }
                        }
                    } else {
                        const weekNum = parseInt(part);
                        if (!isNaN(weekNum) && weekNum >= 1 && weekNum <= 53) {
                            weeksToCheck.add(weekNum);
                        }
                    }
                });
            }

            // If no specific weeks, fallback to current week
            if (weeksToCheck.size === 0) {
                weeksToCheck.add(getWeekNumber(currentDate));
            }

            logger.debug('[EditScheduleItemModal] Checking conflicts for weeks:', Array.from(weeksToCheck));

            // Aggregate conflicts from all weeks (same logic as week picker)
            const allConflicts: ConflictReason[] = [];

            // Convert Set to Array for iteration (TypeScript compatibility)
            for (const weekNumber of Array.from(weeksToCheck)) {
                // Create a temporary schedule entry for just this week
                const tempScheduleEntryData = {
                    id: formData.id || 'temp-conflict-check',
                    day: formData.day,
                    timeStart: timeStart,
                    timeEnd: timeEnd,
                    teacher: formData.teacher || '',
                    groups: formData.groups || [],
                    room: formData.room || '',
                    subject: formData.subject || formData.course || '',
                    course: formData.subject || formData.course || '', // Backward compatibility
                    type: formData.type || 'class',
                    weekSeries: weekNumber.toString(), // Single week for this check
                    period_id: formData.period_id ?? formData.periodId ?? null
                };

                const tempScheduleEntry = new ScheduleEntry(tempScheduleEntryData as any);

                if (!tempScheduleEntry.isTimeValid) {
                    continue;
                }

                // Use ConflictManager to calculate conflicts for this week
                const entryWithConflicts = conflictManager.calculateConflictForEntry(
                    tempScheduleEntry,
                    scheduleItems,
                    teachersList,
                    groupsList,
                    subjectsList
                );

                // Get conflict info for this specific week
                const weekKey = `${weekNumber}-${currentYear}`;
                const weekConflictInfo = entryWithConflicts.conflictsByWeek?.get(weekKey);

                if (weekConflictInfo && weekConflictInfo.reasons) {
                    // Add conflicts with week context
                    weekConflictInfo.reasons.forEach((reason: ConflictReason) => {
                        // Only include red and orange conflicts
                        if (reason.severity === 'red' || reason.severity === 'orange') {
                            // Add week context to the reason text if multiple weeks
                            const contextualReason = weeksToCheck.size > 1
                                ? { ...reason, reason: `Week ${weekNumber}: ${reason.reason}` }
                                : reason;
                            allConflicts.push(contextualReason);
                        }
                    });
                }
            }

            // Remove duplicate conflicts (same reason text)
            const uniqueConflicts = allConflicts.filter((conflict, index, self) =>
                index === self.findIndex(c => c.reason === conflict.reason)
            );

            logger.debug('[EditScheduleItemModal] Aggregated conflicts from all weeks:', {
                totalWeeksChecked: weeksToCheck.size,
                totalConflicts: allConflicts.length,
                uniqueConflicts: uniqueConflicts.length,
                conflicts: uniqueConflicts
            });

            setConflicts(uniqueConflicts);

        } catch (error) {
            logger.error('[EditScheduleItemModal] Error checking conflicts:', error);
            setConflicts([]);
        }
    }, [localEditTimeValue, isAdmin, currentYear, currentDate]); // Simplified dependencies

    // Function to handle conflict icon hover
    const handleConflictIconEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (conflicts.length === 0) return;

        const rect = e.currentTarget.getBoundingClientRect();
        setPopoverPosition({
            x: rect.left + rect.width / 2,
            y: rect.bottom + 4
        });
        setShowConflictPopover(true);
    }, [conflicts.length]);

    const handleConflictIconLeave = useCallback(() => {
        setShowConflictPopover(false);
    }, []);

    // Handle click outside popover
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node) &&
                conflictIconRef.current && !conflictIconRef.current.contains(event.target as Node)) {
                setShowConflictPopover(false);
            }
        };

        if (showConflictPopover) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showConflictPopover]);

    // Check conflicts when form data changes
    useEffect(() => {

        if (editFormData && editFormData.type === 'class' && isAdmin && isLocalTimeValid) {
            logger.debug('[EditScheduleItemModal] Setting timeout for conflict check');
            // Debounce conflict checking to avoid too many calls
            const timeoutId = setTimeout(() => {
                logger.debug('[EditScheduleItemModal] Timeout executed, calling checkConflicts');
                // Create enhanced form data with required arrays
                const enhancedFormData: EditFormData = {
                    ...editFormData,
                    _baseScheduleItems: baseScheduleItems,
                    _teachers: localTeachers,
                    _groups: localGroups,
                    _subjects: localSubjects
                };
                checkConflicts(enhancedFormData);
            }, 300);

            return () => {
                clearTimeout(timeoutId);
            };
        } else {
            setConflicts([]);
            return; // Add explicit return
        }
    }, [editFormData, isLocalTimeValid, checkConflicts, isAdmin, baseScheduleItems, localTeachers, localGroups, localSubjects]);

    // Get current event/meeting date for date picker (single-week selections)
    const currentEventDate = (editFormData?.type === 'event' || editFormData?.type === 'meeting') && isSingleWeekSelected(editFormData?.weekSeries) && editFormData?.day
        ? convertScheduleFormatToDate(editFormData.weekSeries || '', editFormData.day || '', editFormData.period_id ?? editFormData.periodId)
        : '';

    // Format invited teachers for meeting preview
    const meetingInvitedTeachers = useMemo(() => {
        if (editFormData?.type !== 'meeting') return '';
        const raw = (editFormData as any)?.teachers;
        if (!raw || !Array.isArray(raw) || raw.length === 0) return '';
        const names = raw
            .map((t: any) => {
                if (typeof t === 'string') return t;
                if (t && typeof t === 'object') return t.name || t.teacher_name || '';
                return '';
            })
            .filter(Boolean);
        const unique = Array.from(new Set(names));
        return unique.join(', ');
    }, [editFormData]);

    if (!selectedScheduleItem || !editFormData) {
        return null;
    }

    // Check if we're creating a new item vs editing an existing one
    const isCreatingNew = selectedScheduleItem?.id ? String(selectedScheduleItem.id).startsWith('temp-add-') : false;

    const modalTitle = isAdmin && editFormData && selectedScheduleItem ? (
        groupSpecificMode?.groupData?.id !== undefined ? (
            <span className="inline-flex items-center">
                <span>{isCreatingNew ? `Add Class for ${groupSpecificMode.groupData.name || 'Group'}` : `Edit Class for ${groupSpecificMode.groupData.name || 'Group'}`}</span>
                <HelpIcon documentationId="schedule-edit" size={18} position="relative" className="ml-2" />
                {editFormData.type === 'class' && conflicts.length > 0 && (
                    <span
                        ref={conflictIconRef}
                        className="ml-2 cursor-help"
                        onMouseEnter={handleConflictIconEnter}
                        onMouseLeave={handleConflictIconLeave}
                    >
                        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </span>
                )}
                {/* auto (beta) button moved under header */}
            </span>
        ) : exceptionInfo ? (
            <span className="inline-flex items-center">
                <span>{exceptionCreationWeek
                    ? `Create Exception for Week ${exceptionInfo.week}`
                    : `Edit Schedule Item (Week ${exceptionInfo.week} Exception)`
                }</span>
                <HelpIcon documentationId="schedule-edit-exception" size={18} position="relative" className="ml-2" />
                <HelpIcon documentationId="schedule-edit" size={18} position="relative" className="ml-2" />
                {editFormData.type === 'class' && conflicts.length > 0 && (
                    <span
                        ref={conflictIconRef}
                        className="ml-2 cursor-help"
                        onMouseEnter={handleConflictIconEnter}
                        onMouseLeave={handleConflictIconLeave}
                    >
                        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </span>
                )}
                {/* auto (beta) button moved under header */}
            </span>
        ) : (
            <span className="inline-flex items-center">
                <span>Edit Schedule Item</span>
                <HelpIcon documentationId="schedule-edit" size={18} position="relative" className="ml-2" />
                {editFormData.type === 'class' && conflicts.length > 0 && (
                    <span
                        ref={conflictIconRef}
                        className="ml-2 cursor-help"
                        onMouseEnter={handleConflictIconEnter}
                        onMouseLeave={handleConflictIconLeave}
                    >
                        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </span>
                )}
                {/* auto (beta) button moved under header */}
            </span>
        )
    ) : selectedScheduleItem ? (selectedScheduleItem.course || selectedScheduleItem.title || 'Schedule Details') : 'Schedule Details';

    // Disable save when all weeks are untoggled for class/meeting type
    const isWeekSelectionEmpty = (editFormData?.type === 'class' || editFormData?.type === 'meeting') && (!editFormData?.weekSeries || editFormData.weekSeries.trim() === '');
    const isSaveDisabled = isWeekSelectionEmpty;

    // Compute save button title with disabled tooltip override
    const defaultSaveTitle = (
        groupSpecificMode?.directPublish
            ? (isCreatingNew ? 'Add this class immediately' : 'Save changes immediately')
            : draftMode
                ? (isCreatingNew ? 'Add this item to the draft' : 'Save changes to the draft')
                : (isCreatingNew ? 'Clicking will add the item to the review queue. It will not be published until you review.' : 'Clicking this button will add the changes to the review queue. They will not be published until you review them.')
    );
    const saveButtonTitle = isSaveDisabled ? 'at least one week must be active' : defaultSaveTitle;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={modalTitle}
            inline={renderInline}
            containerClassName={modalContainerClassName}
        >
            {isAdmin && editFormData && selectedScheduleItem ? (
                <form onSubmit={handleSaveEdit} className="space-y-4">
                    {/* Exception Warning */}
                    {exceptionInfo && (
                        <div className={`border rounded-md p-3 mb-4 ${exceptionCreationWeek
                            ? 'bg-yellow-50 border-yellow-300'
                            : 'bg-blue-50 border-blue-200'
                            }`}>
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className={`h-5 w-5 ${exceptionCreationWeek
                                        ? 'text-yellow-500'
                                        : 'text-blue-400'
                                        }`} viewBox="0 0 20 20" fill="currentColor">
                                        {exceptionCreationWeek ? (
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        ) : (
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        )}
                                    </svg>
                                </div>
                                <div className="ml-3 flex-1">
                                    <h3 className={`text-sm font-medium ${exceptionCreationWeek
                                        ? 'text-yellow-900'
                                        : 'text-blue-800'
                                        }`}>
                                        {exceptionCreationWeek
                                            ? `Creating Exception for Week ${exceptionInfo.week}`
                                            : `Editing Week ${exceptionInfo.week} Exception`
                                        }
                                    </h3>
                                    <div className={`mt-2 text-sm ${exceptionCreationWeek
                                        ? 'text-yellow-800'
                                        : 'text-blue-700'
                                        }`}>
                                        {exceptionCreationWeek ? (
                                            <p className="font-medium">
                                                ⚠️ You are creating an exception for week {exceptionInfo.week}.
                                                <br />
                                                <span className="font-bold">All changes you make will ONLY apply to week {exceptionInfo.week}.</span>
                                                <br />
                                                The rest of the schedule will remain unchanged.
                                            </p>
                                        ) : (
                                            <p>Changes to time, teacher, or room will only apply to week {exceptionInfo.week}. Other changes will apply to the entire schedule entry.</p>
                                        )}
                                    </div>
                                    {!exceptionCreationWeek && (
                                        <div className="mt-3">
                                            <button
                                                type="button"
                                                onClick={handleRemoveException}
                                                className="inline-flex items-center px-3 py-1 border border-blue-300 text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            >
                                                Remove Exception
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Item Type - Button Toggle and Auto Scheduling */}
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">Type <HelpIcon documentationId="schedule-type" size={14} position="relative" className="ml-2" /></label>
                            <div className="flex space-x-2">
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('class')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${editFormData.type === 'class' || !editFormData.type
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Class
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('event')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${editFormData.type === 'event'
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Event
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('meeting')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${editFormData.type === 'meeting'
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Meeting
                                </button>
                            </div>
                        </div>
                        {isAdmin && editFormData && selectedScheduleItem && (editFormData.type === 'class' || !editFormData.type) && (
                            <div className="ml-4 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleOpenAutoScheduler}
                                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm font-medium bg-transparent"
                                    title="Get intelligent schedule suggestions"
                                    aria-label="Open Auto Scheduler"
                                >
                                    <RobotIcon className="w-4 h-4" />
                                    <span>auto scheduling (beta)</span>
                                </button>
                                <HelpIcon
                                    documentationId="auto-scheduling"
                                    size={14}
                                    position="relative"
                                    className="text-gray-500 hover:text-blue-600"
                                />
                            </div>
                        )}
                    </div>

                    {/* Subject/Name and Teacher/Host/Invites - Same row */}
                    <div className="flex gap-4">
                        {/* Subject/Event/Meeting Name - Left side */}
                        <div className="flex-1">
                            {editFormData.type === 'event' ? (
                                <>
                                    <label htmlFor="edit-event-name" className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                                    <Input
                                        id="edit-event-name"
                                        name="eventName"
                                        value={editFormData.eventName || editFormData.title || ''}
                                        onChange={handleEditFormChange}
                                        placeholder="e.g., School Assembly, Guest Lecture"
                                        required
                                        label=""
                                        autoFocus={false}
                                        minLength={0}
                                    />
                                </>
                            ) : editFormData.type === 'meeting' ? (
                                <>
                                    <label htmlFor="edit-meeting-subject" className="block text-sm font-medium text-gray-700 mb-1">Meeting Subject</label>
                                    <Input
                                        id="edit-meeting-subject"
                                        name="title"
                                        value={editFormData.title || ''}
                                        onChange={handleEditFormChange}
                                        placeholder="e.g., Department Sync, Parent-Teacher Review"
                                        required
                                        label=""
                                        autoFocus={false}
                                        minLength={0}
                                    />
                                </>
                            ) : (
                                <>
                                    <label htmlFor="edit-subject" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">Subject <HelpIcon documentationId="subjects-basics" size={14} position="relative" className="ml-2" /></label>
                                    <SubjectSelector
                                        subjects={localSubjects}
                                        rooms={rooms}
                                        selectedSubjectId={(editFormData as any).subject?.id ?? (editFormData as any).subject_id ?? null}
                                        onChange={(subjectId, subjectData) => {
                                            const subj = subjectData || localSubjects.find(s => String(s.id) === String(subjectId));
                                            setEditFormData(prev => ({
                                                ...prev!,
                                                subject: subj ? { id: subj.id, name: subj.name } : null,
                                                course: subj?.name || prev?.course || '',
                                                subject_id: subj?.id
                                            } as any));
                                        }}
                                        onAddSubject={handleAddSubjectWrapper}
                                        placeholder="Select Subject"
                                    />
                                </>
                            )}
                        </div>

                        {/* Teacher/Host/Invited - Right side */}
                        <div className="flex-1">
                            <label htmlFor="edit-teacher" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                {editFormData.type === 'event' ? 'Host' : editFormData.type === 'meeting' ? 'Host (optional) & Invited Teachers' : 'Teachers'} <HelpIcon documentationId="teachers-basics" size={14} position="relative" className="ml-2" />
                            </label>
                            {editFormData.type === 'event' ? (
                                <Input
                                    id="edit-host"
                                    name="host"
                                    value={editFormData.host || ''}
                                    onChange={handleEditFormChange}
                                    placeholder="e.g., Guest Speaker, Admin Team"
                                    label=""
                                    required={false}
                                    autoFocus={false}
                                    minLength={0}
                                />
                            ) : editFormData.type === 'meeting' ? (
                                <div className="space-y-3">
                                    <Input
                                        id="edit-meeting-host"
                                        name="host"
                                        value={editFormData.host || ''}
                                        onChange={handleEditFormChange}
                                        placeholder="Host (optional)"
                                        label=""
                                        required={false}
                                        autoFocus={false}
                                        minLength={0}
                                    />
                                    <div>
                                        <p className="text-xs text-gray-600 mb-1">Invited teachers</p>
                                        <TeacherSelector
                                            teachers={localTeachers}
                                            selectedTeachers={((editFormData as any).teachers || []).map((teacher: any) => {
                                                if (typeof teacher === 'object' && teacher.id) {
                                                    return teacher;
                                                }
                                                const foundTeacher = localTeachers.find(t => String(t.id) === String(teacher));
                                                return foundTeacher ? {
                                                    id: foundTeacher.id,
                                                    name: foundTeacher.name,
                                                    email: foundTeacher.email
                                                } : null;
                                            }).filter(Boolean)}
                                            onChange={(selectedTeachers) => {
                                                const teachersForForm = selectedTeachers.map(teacher => ({
                                                    id: teacher.id,
                                                    name: teacher.name
                                                }));

                                                setEditFormData(prev => ({
                                                    ...prev!,
                                                    teachers: teachersForForm,
                                                    // Update legacy teacher field for backward compatibility
                                                    teacher: teachersForForm.length > 0 ? teachersForForm[0]?.name || null : null,
                                                    teacher_id: teachersForForm.length > 0 ? teachersForForm[0]?.id : undefined
                                                } as any));
                                            }}
                                            onAddTeacher={handleAddTeacherWrapper}
                                            placeholder="Select invited teachers"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <TeacherSelector
                                        teachers={localTeachers}
                                        selectedTeachers={((editFormData as any).teachers || []).map((teacher: any) => {
                                            if (typeof teacher === 'object' && teacher.id) {
                                                return teacher;
                                            }
                                            const foundTeacher = localTeachers.find(t => String(t.id) === String(teacher));
                                            return foundTeacher ? {
                                                id: foundTeacher.id,
                                                name: foundTeacher.name,
                                                email: foundTeacher.email
                                            } : null;
                                        }).filter(Boolean)}
                                        onChange={(selectedTeachers) => {
                                            // Convert to the format expected by the form
                                            const teachersForForm = selectedTeachers.map(teacher => ({
                                                id: teacher.id,
                                                name: teacher.name
                                            }));

                                            setEditFormData(prev => ({
                                                ...prev!,
                                                teachers: teachersForForm,
                                                // Update legacy teacher field for backward compatibility
                                                teacher: teachersForForm.length > 0 ? teachersForForm[0]?.name || null : null,
                                                teacher_id: teachersForForm.length > 0 ? teachersForForm[0]?.id : undefined
                                            } as any));
                                        }}
                                        onAddTeacher={handleAddTeacherWrapper}
                                        placeholder="Select Teachers"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Groups - Fourth field */}
                    {editFormData.type === 'meeting' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                Groups <HelpIcon documentationId="groups-basics" size={14} position="relative" className="ml-2" />
                            </label>
                            <div className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-md p-3 bg-gray-50">
                                Meetings are teacher-oriented and do not target student groups.
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label htmlFor="edit-groups" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                Groups {editFormData.type === 'event' ? '(Optional)' : ''} <HelpIcon documentationId="groups-basics" size={14} position="relative" className="ml-2" />
                            </label>
                            <GroupSelector
                                groups={localGroups}
                                selectedGroups={editFormData.groups || []}
                                onChange={(selectedGroups) => {
                                    // In group-specific mode, ensure the specified group is always included
                                    if (groupSpecificMode?.groupData?.id !== undefined && groupSpecificMode.disableGroupRemoval !== false) {
                                        const requiredGroupId = groupSpecificMode.groupData.id;
                                        const hasRequiredGroup = selectedGroups.some(g => {
                                            if (typeof g === 'object' && g !== null) {
                                                return (g as any).id === requiredGroupId;
                                            }
                                            return g === requiredGroupId;
                                        });

                                        if (!hasRequiredGroup) {
                                            // Add the required group back to the beginning of the array
                                            selectedGroups = [{ id: requiredGroupId, name: groupSpecificMode.groupData.name, group_code: groupSpecificMode.groupData.group_code }, ...selectedGroups];
                                        }
                                    }

                                    setEditFormData(prev => ({ ...prev!, groups: selectedGroups }));
                                }}
                                onAddGroup={handleAddGroupWrapper}
                                placeholder={editFormData.type === 'event' ? "All Groups (leave empty for all)" : "Select Groups"}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {groupSpecificMode?.groupData?.id !== undefined
                                    ? `${groupSpecificMode.groupData.name || 'Group'} is automatically included. You can select additional groups.`
                                    : (editFormData.type === 'event'
                                        ? "Leave empty for all groups, or select specific groups"
                                        : "Select one or more groups"
                                    )
                                }
                            </p>
                        </div>
                    )}

                    {/* Event date / class period+day+time row — above week series */}
                    <div className="flex gap-4">
                        {(editFormData.type === 'event' || editFormData.type === 'meeting') && (
                            <div className="flex-none w-56">
                                <label htmlFor="edit-event-date-inline" className="block text-sm font-medium text-gray-700 mb-1">
                                    {isSingleWeekSelected(editFormData.weekSeries) ? `${editFormData.type === 'meeting' ? 'Meeting' : 'Event'} Date` : 'Select date'}
                                </label>
                                <Input
                                    id="edit-event-date-inline"
                                    name="eventDate"
                                    type="date"
                                    value={currentEventDate}
                                    onChange={handleEventDateChange}
                                    required
                                    placeholder=""
                                    label=""
                                    autoFocus={false}
                                    minLength={0}
                                />
                            </div>
                        )}
                        {editFormData.type === 'class' ? (
                            <EditScheduleItemTimeSection
                                variant="class"
                                academicYearRangeLabel={`${getAcademicYearStartForForm()}–${getAcademicYearStartForForm() + 1}`}
                                classEditPeriodSelect={classEditPeriodSelect}
                                editFormPeriodOptions={editFormPeriodOptions}
                                resolvedEditPeriodLabelForOrphan={resolvedEditPeriod.label ?? ''}
                                onPeriodChange={handleEditPeriodSelectChange}
                                periodSelectionDisabled={isPeriodSelectionDisabled}
                                periodSelectionDisabledReason={currentTimeRangeContext?.periodSelectionDisabledReason || ''}
                                dayValue={editFormData.day || ''}
                                activeDays={activeDays}
                                onDayChange={handleEditFormChange}
                                value={localEditTimeValue}
                                onChange={handleTimeInputChange}
                                onBlur={handleTimeBlur}
                                isValid={isLocalTimeValid}
                                minHour={institutionSettings.minHour}
                                maxHour={institutionSettings.maxHour}
                                required
                            />
                        ) : (
                            <EditScheduleItemTimeSection
                                variant="timeOnly"
                                value={localEditTimeValue}
                                onChange={handleTimeInputChange}
                                onBlur={handleTimeBlur}
                                isValid={isLocalTimeValid}
                                minHour={institutionSettings.minHour}
                                maxHour={institutionSettings.maxHour}
                                required
                            />
                        )}
                    </div>

                    {/* Week Series - Fifth field (Classes/Meetings) */}
                    {editFormData.type === 'class' ? (
                        <div>
                            <label htmlFor="edit-week-series" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                Week Series
                                <HelpIcon documentationId="week-series-picker" size={16} position="relative" className="ml-2" />
                            </label>
                            <ScheduleEntryWeekPicker
                                value={editFormData.weekSeries || ''}
                                onChange={(weekSeriesString: string) => {
                                    setEditFormData(prev => ({ ...prev!, weekSeries: weekSeriesString }));
                                }}
                                currentYear={getAcademicYearStartForForm()}
                                resolvedPeriod={resolvedEditPeriod}
                                scheduleDay={editFormData.day || ''}
                                exceptionsData={originalEntry ? {
                                    exceptions: Array.isArray(originalEntry.exceptions) ? originalEntry.exceptions : [],
                                    originalEntry: originalEntry
                                } as any : null}
                                conflictDetectionData={isAdmin && editFormData.type === 'class' ? {
                                    formData: { ...editFormData, time: localEditTimeValue },
                                    baseScheduleItems: baseScheduleItems,
                                    teachers: teachers,
                                    groups: groups,
                                    subjects: subjects
                                } as any : null}
                                isConflictDetectionEnabled={isAdmin && editFormData.type === 'class' && isLocalTimeValid}
                                onCreateException={handleCreateExceptionForWeek}
                            />
                            {outOfPeriodWeeks.length > 0 && (
                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                    Weeks {outOfPeriodWeeks.join(', ')} are outside {resolvedEditPeriod.label}. The entry can still be saved.
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Events/meetings: date + time are above; description follows */
                        <>
                            {/* Event Description */}
                            <div>
                                <label htmlFor="edit-event-description" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    {editFormData.type === 'meeting' ? 'Meeting Description (Optional)' : 'Description (Optional)'}
                                    <HelpIcon documentationId="event-description-format" size={14} position="relative" className="ml-2" />
                                </label>
                                <textarea
                                    id="edit-event-description"
                                    name="description"
                                    value={editFormData.description || ''}
                                    onChange={handleEditFormChange}
                                    placeholder={editFormData.type === 'meeting' ? 'Add details about the meeting...' : 'Add details about the event...'}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-vertical"
                                />
                                <div className="mt-2 flex justify-end">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleOpenEventPreview}
                                        className="text-sm"
                                        title={`Preview how this ${editFormData.type === 'meeting' ? 'meeting' : 'event'} will appear to users`}
                                        aria-label={editFormData.type === 'meeting' ? 'Preview Meeting' : 'Preview Event'}
                                    >
                                        {editFormData.type === 'meeting' ? 'Preview Meeting' : 'Preview Event'}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Room */}
                    <div>
                        <label htmlFor="edit-room" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">Room <HelpIcon documentationId="rooms-basics" size={14} position="relative" className="ml-2" /></label>
                        <div className="flex flex-col gap-2">
                            <select
                                id="edit-room"
                                name="room"
                                value={String((editFormData as any).room?.id ?? (editFormData as any).room_id ?? '')}
                                onChange={(e) => {
                                    const selectedId = e.target.value ? Number(e.target.value) : undefined;
                                    const rm = rooms.find(r => String(r.id) === String(selectedId));
                                    setEditFormData(prev => ({
                                        ...prev!,
                                        room: rm ? { id: rm.id, name: rm.name || rm.room_code } : null,
                                        room_id: rm?.id
                                    } as any));
                                }}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            >
                                <option value="">-- Select Room --</option>
                                {filteredRooms.map(room => {
                                    const roomName = room.name || room.room_code || String(room.id);
                                    return <option key={String(room.id)} value={String(room.id)}>{roomName}</option>;
                                })}
                            </select>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex justify-between">
                        <div className="flex space-x-3">
                            {onDelete && !isCreatingNew && (
                                <Button
                                    type="button"
                                    variant="danger"
                                    onClick={handleDeleteClick}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    title="Delete this schedule item"
                                    aria-label="Delete schedule item"
                                >
                                    Delete
                                </Button>
                            )}
                            {!isCreatingNew && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setShowDetailView(true)}
                                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                    title="View detailed information"
                                    aria-label="View schedule item details"
                                >
                                    View Details
                                </Button>
                            )}
                        </div>
                        <div className="flex space-x-3">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                title="Cancel editing"
                                aria-label="Cancel"
                            >
                                Cancel
                            </Button>
                            <div className="relative group">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    onClick={() => { }}
                                    title={saveButtonTitle}
                                    disabled={isSaveDisabled}
                                    aria-label={
                                        groupSpecificMode?.directPublish
                                            ? (isCreatingNew ? 'Add Class' : 'Save Changes')
                                            : draftMode
                                                ? 'Save'
                                                : (isCreatingNew ? 'Create' : 'Add Changes to Review')
                                    }
                                >
                                    {groupSpecificMode?.directPublish
                                        ? (isCreatingNew ? 'Add Class' : 'Save Changes')
                                        : draftMode
                                            ? 'Save'
                                            : (isCreatingNew ? 'Add to Review' : 'Add Changes to Review')
                                    }
                                </Button>
                                {isSaveDisabled && (
                                    <div
                                        className="absolute -top-12 right-0 z-50 bg-black text-white text-sm rounded-md px-3 py-2 shadow-lg hidden group-hover:block whitespace-nowrap max-w-none"
                                        role="tooltip"
                                    >
                                        at least one week must be active
                                    </div>
                                )}
                                {!groupSpecificMode?.directPublish && !draftMode && (
                                    <HelpIcon
                                        documentationId="schedule-submit-button"
                                        position="inline"
                                        size={14}
                                        className="ml-2"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            ) : selectedScheduleItem ? (
                <div className="space-y-4">
                    {/* Display mode */}
                    {selectedScheduleItem.type === 'event' ? (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700">Event Date</h3>
                            <p className="mt-1">{convertScheduleFormatToDate(selectedScheduleItem.weekSeries, selectedScheduleItem.day, selectedScheduleItem.periodId) || 'N/A'}</p>
                        </div>
                    ) : (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700">Week Series</h3>
                            <p className="mt-1">{selectedScheduleItem.weekSeries}</p>
                        </div>
                    )}
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">Period</h3>
                        <p className="mt-1">{selectedScheduleItem.resolvePeriodScope(periodResolutionStructure).label}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">Groups</h3>
                        <p className="mt-1">{
                            selectedScheduleItem.groups?.length > 0 ?
                                selectedScheduleItem.groups?.map(g => {
                                    // Handle case where g is already a group object
                                    if (typeof g === 'object' && g !== null) {
                                        return (g as Group).name || (g as Group).group_code || String((g as Group).id);
                                    }
                                    // Handle case where g is an ID or name string
                                    const group = groups.find(gd => gd.id === g || gd.name === g || gd.group_code === g);
                                    return group ? (group.name || group.group_code) : String(g);
                                }).join(', ') :
                                'All Groups'
                        }</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">Day</h3>
                        <p className="mt-1 capitalize">{selectedScheduleItem.day}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">Time</h3>
                        <p className="mt-1">{selectedScheduleItem.timeRangeDisplay ? selectedScheduleItem.timeRangeDisplay() : (selectedScheduleItem as any).time}</p>
                    </div>
                    {selectedScheduleItem.type === 'event' ? (
                        <>
                            <div>
                                <h3 className="text-sm font-medium text-gray-700">Event Name</h3>
                                <p className="mt-1">{selectedScheduleItem.title || selectedScheduleItem.eventName || 'N/A'}</p>
                            </div>
                            {selectedScheduleItem.description && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700">Description</h3>
                                    <p className="mt-1 text-gray-600 whitespace-pre-wrap">{selectedScheduleItem.description}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700">Subject</h3>
                            <p className="mt-1">{selectedScheduleItem.subject || selectedScheduleItem.course}</p>
                        </div>
                    )}
                    {selectedScheduleItem.type === 'meeting' && selectedScheduleItem.description && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700">Description</h3>
                            <p className="mt-1 text-gray-600 whitespace-pre-wrap">{selectedScheduleItem.description}</p>
                        </div>
                    )}
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">{selectedScheduleItem.type === 'event' ? 'Host' : 'Teacher'}</h3>
                        <p className="mt-1">{selectedScheduleItem.teacher || 'N/A'}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">Room</h3>
                        <p className="mt-1">{selectedScheduleItem.room}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-700">Type</h3>
                        <p className="mt-1 capitalize">{selectedScheduleItem.type || 'class'}</p>
                    </div>
                    <div className="mt-6 flex justify-between">
                        <Button
                            variant="secondary"
                            onClick={() => setShowDetailView(true)}
                            className="text-blue-600 border-blue-600 hover:bg-blue-50"
                            title="View detailed information"
                            aria-label="View details"
                        >
                            View Details
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleClose}
                            title="Close modal"
                            aria-label="Close"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            ) : null}

            {/* Schedule Entry Detail View Modal */}
            {selectedScheduleItem && (
                <ScheduleEntryDetailView
                    isOpen={showDetailView}
                    onClose={() => setShowDetailView(false)}
                    scheduleEntry={selectedScheduleItem}
                    groups={groups as any}
                    subjects={subjects as any}
                    teachers={teachers as any}
                    rooms={rooms as any}
                />
            )}

            {/* Week Change Confirmation Modal */}
            {exceptionInfo && selectedScheduleItem && (
                <WeekChangeConfirmationModal
                    isOpen={showWeekChangeModal}
                    onClose={handleWeekChangeCancel}
                    onConfirm={handleWeekChangeConfirm}
                    scheduleItem={selectedScheduleItem}
                    currentWeekNumber={exceptionInfo.week}
                    currentYear={exceptionInfo.year}
                />
            )}

            {/* Conflict Popover */}
            {showConflictPopover && conflicts.length > 0 && selectedScheduleItem && (
                <Popover
                    reasons={conflicts}
                    position={popoverPosition}
                    onClose={() => setShowConflictPopover(false)}
                    popoverRef={popoverRef as React.RefObject<HTMLDivElement>}
                />
            )}

            {/* Toast Container */}
            {/* Remove Toast Container - we're using snackbar now */}

            {/* Conflict Snackbar - persistent bar at bottom */}
            {conflicts.length > 0 && editFormData?.type === 'class' && isAdmin && (
                <div className="fixed bottom-0 left-0 right-0 bg-red-50 border-t-2 border-red-200 px-4 py-3 shadow-lg z-50">
                    <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-red-800 font-medium">
                                {(() => {
                                    const redConflicts = conflicts.filter(c => c.severity === 'red').length;
                                    const orangeConflicts = conflicts.filter(c => c.severity === 'orange').length;

                                    if (redConflicts > 0 && orangeConflicts > 0) {
                                        return `${conflicts.length} conflicts detected (${redConflicts} critical, ${orangeConflicts} warnings)`;
                                    } else if (redConflicts > 0) {
                                        return `${redConflicts} critical conflict${redConflicts > 1 ? 's' : ''} detected`;
                                    } else {
                                        return `${orangeConflicts} warning${orangeConflicts > 1 ? 's' : ''} detected`;
                                    }
                                })()}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                            onClick={() => {
                                // Show details in a simple alert for now, could be enhanced later
                                const conflictDetails = conflicts.map(c => `• ${c.reason}`).join('\n');
                                alert(`Conflicts:\n\n${conflictDetails}`);
                            }}
                        >
                            View Details
                        </button>
                    </div>
                </div>
            )}

            {/* AutoScheduler Modal */}
            {editFormData && (
                <AutoScheduler
                    isOpen={showAutoScheduler}
                    onClose={handleCloseAutoScheduler}
                    onSuggestionSelect={handleSuggestionSelect}
                    initialFormData={{
                        teacher: String((() => {
                            // Try to get primary teacher from teachers array first
                            const teachersArray = (editFormData as any).teachers;
                            if (teachersArray && Array.isArray(teachersArray) && teachersArray.length > 0) {
                                const primaryTeacher = teachersArray.find((t: any) => t.role === 'primary') || teachersArray[0];
                                return primaryTeacher?.id || primaryTeacher?.name || '';
                            }
                            // Fallback to single teacher
                            return (editFormData as any).teacher?.id ?? (editFormData as any).teacher_id ?? (typeof editFormData.teacher === 'string' ? editFormData.teacher : '');
                        })()),
                        subject: (
                            (editFormData as any).subject?.name ??
                            (typeof editFormData.subject === 'string' ? editFormData.subject : (editFormData.course || ''))
                        ),
                        groups: (editFormData.groups || []).map(g => {
                            if (typeof g === 'object' && g !== null) {
                                return (g as any).id;
                            }
                            return g;
                        }) as (number | string)[],
                        weekSeries: editFormData.weekSeries || '',
                        day: editFormData.day || '',
                        timeStart: editFormData.timeStart || '',
                        timeEnd: editFormData.timeEnd || '',
                        room: (typeof (editFormData as any).room === 'object' && (editFormData as any).room !== null)
                            ? (((editFormData as any).room.name || (editFormData as any).room.room_code) ?? '')
                            : (editFormData as any).room || '',
                        year: resolvedEditPeriod.academicYearStart || getAcademicYearStartForForm(),
                        duration: editFormData.timeStart && editFormData.timeEnd ?
                            calculateDurationInMinutes(editFormData.timeStart, editFormData.timeEnd) : 90
                    }}
                    availableRooms={rooms as any}
                    baseScheduleItems={baseScheduleItems}
                    teachers={localTeachers}
                    groups={localGroups}
                    subjects={localSubjects}
                    currentYear={currentYear}
                    activeDays={activeDays}
                    institutionSettings={institutionSettings}
                    excludeItemId={selectedScheduleItem?.id}
                />
            )}

            {/* Event Preview Modal */}
            {editFormData && (editFormData.type === 'event' || editFormData.type === 'meeting') && (
                <Modal
                    isOpen={showEventPreview}
                    onClose={handleCloseEventPreview}
                    title={(editFormData.eventName || editFormData.title || 'Preview') + (editFormData.type === 'meeting' ? ' (Meeting)' : '')}
                >
                    <div className="space-y-6">
                        <div className="border-b pb-3">
                            <h3 className="text-2xl font-semibold text-gray-900">
                                {editFormData.eventName || editFormData.title || (editFormData.type === 'meeting' ? 'Untitled Meeting' : 'Untitled Event')}
                            </h3>
                            {editFormData.host && (
                                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                                    <UserIcon className="h-4 w-4 text-gray-500" />
                                    <span>Host: {editFormData.host}</span>
                                </div>
                            )}
                            {editFormData.type === 'meeting' && meetingInvitedTeachers && (
                                <div className="mt-1 flex items-start gap-2 text-sm text-gray-600">
                                    <UserIcon className="h-4 w-4 text-gray-500 mt-0.5" />
                                    <span>Teachers invited: {meetingInvitedTeachers}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                            <div className="flex items-center gap-2 min-w-[180px]">
                                <CalendarIcon className="h-5 w-5 text-blue-600" />
                                <div className="text-sm text-gray-900">
                                    {editFormData.weekSeries && editFormData.day
                                        ? formatEventDate(editFormData.weekSeries, editFormData.day, editFormData.period_id ?? editFormData.periodId)
                                        : 'TBD'}
                                </div>
                            </div>
                            <div className="hidden sm:block text-gray-300">|</div>
                            <div className="flex items-center gap-2 min-w-[160px]">
                                <ClockIcon className="h-5 w-5 text-blue-600" />
                                <div className="text-sm text-gray-900">
                                    {localEditTimeValue || (editFormData.timeStart && editFormData.timeEnd
                                        ? `${editFormData.timeStart} - ${editFormData.timeEnd}`
                                        : 'TBD')}
                                </div>
                            </div>
                            <div className="hidden sm:block text-gray-300">|</div>
                            <div className="flex items-center gap-2">
                                <MapPinIcon className="h-5 w-5 text-blue-600" />
                                <div className="text-sm text-gray-900">
                                    {(() => {
                                        const roomObj = (editFormData as any).room;
                                        if (typeof roomObj === 'object' && roomObj !== null) {
                                            return roomObj.name || roomObj.room_code || 'TBD';
                                        }
                                        return editFormData.room || 'TBD';
                                    })()}
                                </div>
                            </div>
                        </div>

                        {editFormData.type !== 'meeting' && editFormData.groups && editFormData.groups.length > 0 && (
                            <div>
                                <div className="text-xs text-gray-500 mb-1">Target Groups</div>
                                <div className="flex flex-wrap gap-2">
                                    {(Array.isArray(editFormData.groups) ? editFormData.groups : [editFormData.groups]).map((g, i) => {
                                        const label = typeof g === 'object' ? (g.name || g.group_code || g.id) : g;
                                        return (
                                            <span key={`${label}-${i}`} className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {editFormData.description && (
                            <div className="border-t pt-4">
                                <div className="text-sm font-medium text-gray-500 mb-1">Description</div>
                                {renderTextWithLinks(editFormData.description)}
                            </div>
                        )}

                        <div className="mt-2 flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={handleCloseEventPreview}
                                title="Close preview"
                                aria-label="Close preview"
                            >
                                Close Preview
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </Modal>
    );
};

export default EditScheduleItemModal; 